import { useMemo } from 'react'
import {
  useQuery,
  useMutation,
  useMutationState,
  useQueryClient,
  type QueryClient,
  type QueryKey,
} from '@tanstack/react-query'
import { toast } from '@atrvd/saas-ui'
import { useOrganization } from '@/contexts/OrganizationContext'
import { getSupabaseClient } from '@/lib/supabase'
import { useAuth } from '@/contexts/AuthContext'
import { CASH_POSITION_SUMMARIES_KEY } from '@/hooks/cashPositionSummaries'
import { BANKING_KEYS, PROJECTION_KEYS, keepPreviousForOrg } from '@/lib/query-keys'
import { createTenantDb } from '@/lib/db-tenant'
import {
  invalidateAfterInstitutionRename,
  invalidateManualAccountRemoval,
} from '@/lib/manual-bank-account-invalidation'
import { invalidateAccountDependents } from '@/lib/ledgersync-sync-invalidation'
import { invalidateAfterManualTransactionWrite } from '@/lib/manual-transaction-invalidation'
// DCP-123: the Daily Banking read reuses the legacy transactions cache key —
// see useDailyBankingTransactions. usePlaid does not import this module.
import { PLAID_KEYS } from '@/hooks/usePlaid'
import { shouldPollFileImports, isImportLive } from '@/lib/file-import-stall'
import { toFacility } from '@/lib/facility-row'
import type {
  CreditCardAvailabilityQuery,
  CreditCardAvailabilityRow,
  SageSyncStateRow,
  TenantPlaidTransaction,
} from '@/lib/db-tenant'
import type { TenantTables } from '@dcp/shared/types/tenant-database'
import {
  explainFacilityDuplicate,
  FACILITY_DUPLICATE_FALLBACK,
  type FacilityDuplicate,
  type SubmittedFacilityValues,
} from '@/lib/facility-duplicate'
import type {
  BankingKpiWarnings,
  BankingKpiResult,
  Facility,
  Pack,
  FacilityCategoryData,
  AccountImportStatus,
  BankingFilters,
  CrossCheckingKpi,
  CrossCheckingTransaction,
  CrossCheckingTab,
  BalanceImportStatus,
  ManualTransactionFilters,
  LocTxnScope,
  AssignableCategory,
  BankTransactionSort,
} from '@/types/banking'
import type {
  BankAccountRow,
  BankAccountInput,
  BankAccountUpdate,
  FacilityInput,
  FacilityUpdate,
  PackInput,
  PackUpdate,
  ManualTransactionInput,
  FileImportRow,
  FileImportInput,
  FacilityCategorySignedTotals,
} from '@/types/banking'
import {
  mockAccountImportStatus,
  mockCrossCheckingKpi,
  mockCrossCheckingTransactions,
} from '@/mocks/bankingData'
import type { LocLine, LocReference } from '@/types/loc-availability'
import type { BankTxnGroup } from '@/components/banking/ViewBankTransactionsDialog'
import { formatDate } from '@dcp/shared/utils/formatters'
import { businessDate } from '@dcp/shared/utils/business-date'
import { roundToCents } from '@dcp/shared'

// The keys now live in `@/lib/query-keys`, which carries no runtime dependencies
// — see that module for why. Re-exported here so the ~12 existing import sites
// keep working; new code should import from `@/lib/query-keys` directly.
export { BANKING_KEYS }

// `LocTxnScope` lives in `@/types/banking` so `@/lib/query-keys` can type the
// scoped cache key without importing this hook module (DCP-333). Re-exported
// here because every caller of {@link useLocTransactions} needs it, and the
// argument's type belongs next to the function that takes it.
export type { LocTxnScope }

/**
 * Hook to fetch banking KPI data.
 * Fetches Bank Balance from the tenant `balances` table (DCP-218 — Plaid
 * abandoned), Outstanding Checks from the sage_checks_outstanding_total RPC, and
 * the Intacct Balance total. Book Balance is derived as Bank Balance minus the
 * outstanding amount, and Book vs. Intacct as Book Balance minus Intacct Balance.
 *
 * A failed Intacct read yields `intacctBalance: null` and `bookVsIntacct: null`
 * (the tiles show "—"); a failed Bank Balance or Outstanding Checks read fails
 * the whole query.
 */
export function useBankingKpi(filters?: BankingFilters) {
  const { currentOrganization } = useOrganization()
  // Keyed on the fields the KPI actually reads, so a Category or start-date
  // change on Daily Banking does not refetch every tile for identical figures.
  const scope: BankingFilters = {
    endDate: filters?.endDate,
    facilityIds: filters?.facilityIds,
    accountIds: filters?.accountIds,
  }
  return useQuery<BankingKpiResult>({
    queryKey: BANKING_KEYS.kpi(currentOrganization?.id, scope),
    queryFn: async () => {
      if (!currentOrganization?.id) {
        throw new Error('No organization selected')
      }

      const tenantDb = createTenantDb(currentOrganization.id)

      // Fetch bank balance and the Intacct balance total in parallel. The
      // Intacct sum is independent — its failure must not break the Bank
      // Balance tile, so it catches its own error and degrades to null.
      //
      // getTotal is deliberately the ONE call without a .catch (DCP-292, Presto
      // #2 — reviewed and kept). It does not throw on a query error: it returns
      // a DbResult whose `.error` is rethrown below, exactly as it did before
      // DCP-292 added the facilities read behind it. Degrading it would mean
      // either showing Bank Balance = 0 (a wrong number is worse than a visible
      // failure for the primary tile) or falling back to an unscoped read, which
      // would silently surface deactivated facilities' money — the very thing
      // AC1 forbids. A facilities read that fails here fails for every other
      // tile too, so the loud failure is the honest signal.
      const [bankResult, intacctResult, coverageResult, intacctFreshResult, syncStateResult] = await Promise.all([
        tenantDb.bankBalance.getTotal(scope),
        // Intacct Balance tile (DCP-159/160): Σ of intacct_balance over active
        // Sage bank accounts (null balances counted as 0 inside the method).
        //
        // DCP-335: scoped to the SELECTED facility filter, like every other tile.
        // The previous version deliberately did NOT scope it, on the grounds that
        // "the imported GL balance is an org-wide figure and sage_bank_accounts has
        // no facility_id". The table has no facility_id, true — but it links to one
        // through location_id → facilities.sage_location_id (UPPER-cased, per
        // DCP-262), a chain three shipped features already rely on: the Intacct
        // Balances table's own filter (DCP-160), the DCP-292 deny set, and the
        // sba_facility CTE of get_cash_position_summary (DCP-308). Leaving the tile
        // unscoped made it contradict the table one inch below it on Balance Import.
        //
        // Still scoped by facility ACTIVITY too (DCP-292). Corollary worth knowing:
        // accounts whose location maps to NO facility are counted org-wide but drop
        // out under a filter, so the per-facility totals need not sum to the
        // unfiltered one — see intacctLocationScope in db-tenant.ts.
        tenantDb.sageBankAccounts
          .sumIntacctBalance({ activeOnly: true, facilityIds: scope.facilityIds })
          .catch((err) => {
            console.error('[useBankingKpi] sumIntacctBalance threw:', err)
            return null
          }),
        // DCP-218 AC.3 freshness signals. Each degrades to null on failure so a
        // missing warning never breaks the numeric tiles.
        // Bank-balance coverage for the target day (Bank/Book tiles).
        tenantDb.bankBalance.getCoverage(scope).catch((err) => {
          console.error('[useBankingKpi] getCoverage threw:', err)
          return null
        }),
        // Intacct per-account sync freshness (Intacct tile). DCP-335: takes the
        // SAME facility scope as the sum above — an unscoped warning next to a
        // scoped total would count accounts that are not on screen.
        tenantDb.sageBankAccounts
          .getIntacctFreshness({ activeOnly: true, facilityIds: scope.facilityIds })
          .catch((err) => {
            console.error('[useBankingKpi] getIntacctFreshness threw:', err)
            return null
          }),
        // Sage sync-state rows (Outstanding Checks tile uses the 'checks' row).
        tenantDb.sageSyncState.list().catch((err) => {
          console.error('[useBankingKpi] sageSyncState.list threw:', err)
          return null
        }),
      ])

      if (bankResult.error) throw new Error(bankResult.error.message)

      const bankBalance = bankResult.data.total

      // Extract the Intacct balance total. A failure is null ("could not be
      // loaded"), never 0: a $0 Intacct Balance would be a plausible-looking
      // wrong figure and would make the variance equal the whole Book Balance.
      let intacctBalance: number | null = null
      if (intacctResult && !intacctResult.error) {
        intacctBalance = intacctResult.data
      } else if (intacctResult?.error) {
        console.error('[useBankingKpi] sumIntacctBalance error:', intacctResult.error.message)
      }

      // Fetch the outstanding checks total via the Postgres RPC.
      // The RPC applies the facility filter but NOT an as-of-date filter —
      // date-scoped outstanding semantics are deferred to DCP-218. bankBalance
      // itself IS scoped to endDate via getTotal above.
      const supabase = getSupabaseClient()
      const { data: outstandingRows, error: outstandingError } = await supabase.rpc(
        'sage_checks_outstanding_total',
        // Pass undefined (not []) for "no facility filter": an empty UUID[] would
        // make the RPC's `f.id = ANY('{}')` always-false and wrongly zero the tile.
        {
          org_id: currentOrganization.id,
          p_facility_ids: scope.facilityIds?.length ? scope.facilityIds : undefined,
        },
      )
      if (outstandingError) throw new Error(outstandingError.message)

      // The RPC returns a single-row array; default to 0 when the org has no
      // effectively-issued checks yet (empty array or missing field).
      const outstandingAmount = outstandingRows?.[0]?.outstanding_amount ?? 0

      // DCP-218 AC.3: compute per-tile "data missing for the current day"
      // warnings from the freshness signals fetched above. Each block is
      // best-effort — a null result (signal fetch failed) simply yields no
      // warning rather than a false positive.
      const warnings: BankingKpiWarnings = computeKpiWarnings({
        coverage: coverageResult && !coverageResult.error ? coverageResult.data : null,
        intacctFreshness: intacctFreshResult && !intacctFreshResult.error ? intacctFreshResult.data : null,
        syncStates: syncStateResult && !syncStateResult.error ? syncStateResult.data : null,
        accountFilterActive: !!scope.accountIds?.length,
        intacctLoaded: intacctBalance !== null,
      })

      const bookBalance = bankBalance - outstandingAmount

      return {
        bankBalance,
        // Outstanding checks reduce the book balance, so the figure is stored
        // negative and the tile renders it with a leading minus as-is.
        // Only effectively-issued checks are counted (voided/paid excluded by RPC).
        outstandingChecks: -outstandingAmount,
        bookBalance,
        // Σ of intacct_balance across the in-scope active Sage bank accounts
        // (DCP-159/160, facility-scoped since DCP-335), populated by the
        // 'sync-balances' edge action. Negative/zero totals are passed through
        // as-is (no clamping) so a credit GL balance reads correctly on the tile.
        intacctBalance,
        // Rounded so float residue of the two sums (0.3 − (0.1 + 0.2)) cannot
        // render as "-$0.00" or a stray cent.
        bookVsIntacct: intacctBalance === null ? null : roundToCents(bookBalance - intacctBalance),
        warnings,
      }
    },
    enabled: !!currentOrganization?.id,
  })
}

/**
 * DCP-218 AC.3: derive per-tile freshness warnings from the raw signals.
 * Pure function (no I/O) so it is unit-testable in isolation. Returns a map
 * keyed by KPI metric; an absent key means the tile's data is complete.
 */
function computeKpiWarnings(signals: {
  coverage: { expected: number; covered: number; targetDate: string } | null
  intacctFreshness: { total: number; failed: number; neverSynced: number } | null
  syncStates: SageSyncStateRow[] | null
  accountFilterActive: boolean
  intacctLoaded: boolean
}): BankingKpiWarnings {
  const warnings: BankingKpiWarnings = {}

  // Bank Balance: warn when active accounts lack a balance for the target date.
  if (signals.coverage && signals.coverage.expected > 0 && signals.coverage.covered < signals.coverage.expected) {
    const { expected, covered, targetDate } = signals.coverage
    const missing = expected - covered
    warnings.bankBalance =
      covered === 0
        ? `No bank balance has been recorded for ${targetDate} yet.`
        : `${missing} of ${expected} bank account${expected === 1 ? '' : 's'} ${missing === 1 ? 'is' : 'are'} missing a balance for ${targetDate}.`
  }

  // Outstanding Checks: warn when the Sage 'checks' sync never ran, failed, or
  // has not refreshed today.
  if (signals.syncStates) {
    const checks = signals.syncStates.find((r) => r.sync_type === 'checks')
    const today = businessDate()
    if (!checks || !checks.last_sync_at) {
      warnings.outstandingChecks = 'Outstanding checks have not been synced from Sage Intacct yet.'
    } else if (checks.sync_status === 'failed') {
      warnings.outstandingChecks = `The last outstanding-checks sync failed${checks.last_error ? `: ${checks.last_error}` : '.'}`
    } else {
      // BOTH SIDES OF THIS COMPARISON MUST USE THE SAME CALENDAR.
      //
      // `last_sync_at` is a TIMESTAMPTZ (an instant), and the previous code
      // reduced it to a day with `.slice(0, 10)` — i.e. the UTC day — while
      // comparing it against a UTC "today". Moving only one side to the
      // business day would manufacture a phantom "not refreshed today"
      // warning every evening: a sync that ran at 20:00 Central is already
      // the NEXT day in UTC, so a UTC sync-day would sit a day AHEAD of the
      // business today (masking real staleness), and the mirror case a day
      // behind. Converting the instant through the same primitive attributes
      // the sync to the business day it actually ran on.
      //
      // Validity is checked BEFORE formatting: `businessDate` deliberately THROWS
      // a TypeError on an invalid Date (silent degradation is the failure mode that
      // primitive exists to remove), and an unguarded call here would take the whole
      // KPI query down over one malformed timestamp. Degrade the way the other
      // signals do — no warning rather than a false one.
      const lastSyncAt = new Date(checks.last_sync_at)
      if (!Number.isNaN(lastSyncAt.getTime())) {
        const lastSyncDay = businessDate(lastSyncAt)
        if (lastSyncDay < today) {
          warnings.outstandingChecks = `Outstanding checks have not been refreshed today (last sync: ${lastSyncDay}).`
        }
      }
    }
  }

  // Intacct Balance: warn when any active account failed to sync or was never synced.
  if (signals.intacctFreshness) {
    const affected = signals.intacctFreshness.failed + signals.intacctFreshness.neverSynced
    if (affected > 0) {
      warnings.intacctBalance = `${affected} account${affected === 1 ? '' : 's'} ${affected === 1 ? 'has' : 'have'} a missing or failed Intacct balance sync.`
    }
  }

  // Book Balance is Bank Balance − Outstanding Checks, so it is incomplete when
  // either input is.
  if (warnings.bankBalance || warnings.outstandingChecks) {
    warnings.bookBalance =
      'Book Balance depends on bank balance and outstanding checks data that is incomplete for today.'
  }

  // Book vs. Intacct deliberately does not inherit the Book or Intacct
  // warnings (both operands already carry their own icon). Its one warning is
  // the Account filter: Intacct (and Outstanding Checks) cannot be scoped to a
  // bank account until bank↔Sage account matching exists (DCP-396), so under
  // that filter the two sides cover different accounts.
  if (signals.accountFilterActive) {
    warnings.bookVsIntacct =
      'The Account filter is not applied to Intacct Balance, so this variance compares different accounts.'
  }

  // A value that could not be loaded renders as "—" and carries no
  // data-quality warning (BA decision F2): there is no figure to qualify.
  if (!signals.intacctLoaded) {
    delete warnings.intacctBalance
    delete warnings.bookVsIntacct
  }

  return warnings
}

/**
 * Hook to fetch manual transactions with filters and pagination. The previous
 * page stays on screen while the next one loads (same organization only).
 */
export function useManualTransactions(filters?: ManualTransactionFilters) {
  const { currentOrganization } = useOrganization()
  return useQuery({
    queryKey: BANKING_KEYS.manualTransactions(currentOrganization?.id, filters),
    placeholderData: keepPreviousForOrg(currentOrganization?.id, 2),
    queryFn: async () => {
      const { createTenantDb } = await import('@/lib/db-tenant')

      if (!currentOrganization?.id) {
        throw new Error('No organization selected')
      }

      const tenantDb = createTenantDb(currentOrganization.id)
      const { data, error } = await tenantDb.manualTransactions.list(filters)
      if (error) throw error
      return data
    },
    enabled: !!currentOrganization?.id,
  })
}

/**
 * Hook to get total manual transaction count for pagination. The previous
 * count stays while a new filter's count loads, so the footer does not drop
 * out for one request; the filter change has already sent the table to page 1,
 * which any count allows.
 */
export function useManualTransactionCount(filters?: ManualTransactionFilters) {
  const { currentOrganization } = useOrganization()
  return useQuery({
    queryKey: BANKING_KEYS.manualTransactionCount(currentOrganization?.id, filters),
    placeholderData: keepPreviousForOrg(currentOrganization?.id, 2),
    queryFn: async () => {
      const { createTenantDb } = await import('@/lib/db-tenant')

      if (!currentOrganization?.id) {
        throw new Error('No organization selected')
      }

      const tenantDb = createTenantDb(currentOrganization.id)
      const { data, error } = await tenantDb.manualTransactions.count(filters)
      if (error) throw error
      return data
    },
    enabled: !!currentOrganization?.id,
  })
}

/**
 * Hook to fetch facility category data for bar chart.
 * Delegates aggregation to the Postgres RPC function `get_facility_category_chart`
 * instead of fetching all transactions client-side.
 *
 * Returns:
 *   - `rows` — pivoted-by-facility chart rows (one row per facility, with
 *     each category as a numeric key).
 *   - `categoryKeys` — distinct categories present in the data (drives the
 *     dynamic <Bar/> rendering).
 *   - `signedTotals` — `facility → category → signed net`, kept SEPARATE from
 *     `rows` (which hold only the plotted magnitude `ABS(net)`) so a category
 *     name can never collide with a reserved key. The tooltip reads the sign
 *     from here — the sign comes from the summed amounts, not the (deprecated,
 *     DCP-250) category type.
 *
 * @param startDate - Optional ISO date string for range start (inclusive)
 * @param endDate - Optional ISO date string for range end (inclusive)
 */
export function useFacilityCategoryData(startDate?: string, endDate?: string) {
  const { currentOrganization } = useOrganization()
  return useQuery<{
    rows: FacilityCategoryData[]
    categoryKeys: string[]
    signedTotals: FacilityCategorySignedTotals
  }>({
    queryKey: BANKING_KEYS.facilityCategories(currentOrganization?.id, startDate, endDate),
    queryFn: async () => {
      const { createTenantDb } = await import('@/lib/db-tenant')

      if (!currentOrganization?.id) {
        throw new Error('No organization selected')
      }

      const tenantDb = createTenantDb(currentOrganization.id)
      const result = await tenantDb.transactions.facilityCategoryChart({ startDate, endDate })

      if (result.error) throw new Error(result.error.message)

      // Transform flat DB rows into chart-compatible format.
      // Each row from the RPC is { facility_name, category, total } where `total`
      // is the SIGNED net — pivoted by facility for the stacked bar chart.
      const categorySet = new Set<string>()
      const aggregation = new Map<string, FacilityCategoryData>()
      // Signed nets live in their own nested map (facility → category → net),
      // NOT mixed into the row alongside category keys, so a user-defined
      // category name can never collide with the signed-value storage.
      const signedTotals: FacilityCategorySignedTotals = {}

      for (const row of result.data) {
        if (!aggregation.has(row.facility_name)) {
          aggregation.set(row.facility_name, { facility: row.facility_name })
        }

        // `row.total` is the SIGNED net per facility × category (DCP-223 part 2).
        // Skip categories that net to exactly zero (incl. facilities with no
        // transactions in range, which COALESCE to 0).
        const net = Number(row.total)
        if (net === 0) continue

        categorySet.add(row.category)

        const entry = aggregation.get(row.facility_name)!
        // Bars stack on the magnitude (keeps the stacked-bar visual clean);
        // the signed net is kept separately for the tooltip so its sign comes
        // from the numbers, not the category type.
        entry[row.category] = Math.abs(net)
        ;(signedTotals[row.facility_name] ??= {})[row.category] = net
      }

      return {
        rows: Array.from(aggregation.values()),
        categoryKeys: Array.from(categorySet),
        signedTotals,
      }
    },
    enabled: !!currentOrganization?.id,
  })
}

/**
 * Hook to fetch account import status
 */
export function useAccountImportStatus() {
  const { currentOrganization } = useOrganization()
  return useQuery<AccountImportStatus[]>({
    queryKey: BANKING_KEYS.importStatus(currentOrganization?.id),
    queryFn: async () => {
      await new Promise((resolve) => setTimeout(resolve, 300))
      return mockAccountImportStatus
    },
  })
}

/**
 * Hook to fetch facilities from the tenant database.
 *
 * DCP-292: deactivated facilities must not appear in any selector/filter/list
 * across the app, so this hook DEFAULTS to active-only. This mirrors the
 * `useSageBankAccounts({ activeOnly })` convention: the safe default hides
 * inactive facilities everywhere, and the two surfaces that legitimately need
 * ALL facilities opt out explicitly:
 *   - the Settings → Facilities management page (where inactive rows are toggled)
 *   - the Intacct balance table's facility-name lookup map (so a row's name
 *     still resolves even if its facility was later deactivated)
 *
 * Filtering is done client-side via React Query's `select` so that both the
 * active-only and the full-list consumers share ONE network fetch and ONE cache
 * entry (keyed only by org) — `select` derives each observer's view from the
 * same cached "all facilities" list.
 *
 * @param options.activeOnly - default true; pass false to include inactive rows.
 */
export function useFacilities(options: { activeOnly?: boolean } = {}) {
  const { currentOrganization } = useOrganization()
  const activeOnly = options.activeOnly ?? true
  // Stable per-observer view: filter to active facilities unless the caller
  // opted into the full list. Memoized on activeOnly so the same reference is
  // reused across renders (React Query re-runs `select` when its identity changes).
  const select = useMemo(
    () =>
      activeOnly ? (data: Facility[]) => data.filter((f) => f.is_active) : undefined,
    [activeOnly],
  )
  return useQuery<Facility[], Error, Facility[]>({
    queryKey: BANKING_KEYS.facilities(currentOrganization?.id),
    queryFn: async () => {
      // Import createTenantDb here to avoid circular dependency
      const { createTenantDb } = await import('@/lib/db-tenant')

      if (!currentOrganization?.id) {
        throw new Error('No organization selected')
      }

      const tenantDb = createTenantDb(currentOrganization.id)
      const { data, error } = await tenantDb.facilities.list()
      if (error) throw new Error(error.message)
      return data.map(toFacility)
    },
    enabled: !!currentOrganization?.id,
    select,
  })
}

/**
 * Hook to fetch all bank accounts from tenant database.
 * Used in form selects for manual transaction editing.
 */
export function useBankAccounts(options: { activeOnly?: boolean } = {}) {
  const { currentOrganization } = useOrganization()
  // DCP-292: active-only is opt-IN here (unlike useFacilities). Most consumers
  // are add/edit dialogs / duplicate checks that legitimately need EVERY
  // account, so the default preserves the full list; only the surfaces that
  // must hide inactive-facility banks (Dashboard account filter) pass
  // { activeOnly: true }. The cache is
  // keyed with the flag so the two views don't overwrite each other.
  const activeOnly = options.activeOnly ?? false
  return useQuery<BankAccountRow[]>({
    queryKey: activeOnly
      ? [...BANKING_KEYS.bankAccounts(currentOrganization?.id), 'active']
      : BANKING_KEYS.bankAccounts(currentOrganization?.id),
    queryFn: async () => {
      const { createTenantDb } = await import('@/lib/db-tenant')

      if (!currentOrganization?.id) {
        throw new Error('No organization selected')
      }

      const tenantDb = createTenantDb(currentOrganization.id)
      const { data, error } = await tenantDb.bankAccounts.list({ activeOnly })
      if (error) throw new Error(error.message)
      return data
    },
    enabled: !!currentOrganization?.id,
  })
}

/**
 * Bank account row including the `deleted_at` flag — used only by the
 * Manual Transactions edit dialog (DCP-222) where transactions that
 * reference a soft-deleted bank must still surface the original Bank /
 * Account Name / Account Number with a "(deleted)" marker. It only names
 * the account, so the Cash Position flag is not read for these rows.
 */
export type BankAccountRowWithDeletedAt = Omit<BankAccountRow, 'include_in_cash_position'> & {
  deleted_at: string | null
}

/**
 * Fetch bank account rows by id, **including soft-deleted ones**.
 *
 * Used by the Manual Transactions edit dialog: when a transaction
 * references a bank that was deleted from Settings, the regular
 * `useBankAccounts()` (which filters out `deleted_at IS NOT NULL`)
 * leaves the dropdown without an option for the transaction's current
 * value. This hook pulls the missing rows in so the dialog can render
 * them as a locked "(deleted)" option preserving the historical
 * selection.
 *
 * The query key sorts ids deterministically so identical reference
 * sets share a cache entry across re-renders. The query is disabled
 * when `ids` is empty — most transactions reference only active banks
 * and never trigger this fetch.
 *
 * @param ids - bank_account.id values from the form's open rows
 */
export function useBankAccountsByIds(ids: string[]) {
  const { currentOrganization } = useOrganization()
  // Sort for a deterministic query key — two callers passing the same
  // set in different order share a cache entry. The current edit-dialog
  // caller only ever passes a single id (sort is a no-op for that
  // case), but keeping it unconditional means a future batch caller
  // gets cache reuse for free without revisiting this hook. Memoised
  // so the array reference stays stable across renders when `ids`
  // hasn't changed — keeps the React Query key referentially equal
  // and avoids per-render allocations.
  const sortedIds = useMemo(() => [...ids].sort(), [ids])
  return useQuery<BankAccountRowWithDeletedAt[]>({
    queryKey: ['banking', 'bank-accounts-by-ids', currentOrganization?.id, sortedIds],
    queryFn: async () => {
      const { createTenantDb } = await import('@/lib/db-tenant')

      if (!currentOrganization?.id) {
        throw new Error('No organization selected')
      }

      const tenantDb = createTenantDb(currentOrganization.id)
      const { data, error } = await tenantDb.bankAccounts.listByIdsIncludingDeleted(sortedIds)
      if (error) throw new Error(error.message)
      return data
    },
    enabled: !!currentOrganization?.id && sortedIds.length > 0,
  })
}

/**
 * Hook to fetch cross-checking KPI data
 * Returns summary metrics for cross-checking dashboard
 */
export function useCrossCheckingKpi(filters?: BankingFilters) {
  const { currentOrganization } = useOrganization()
  return useQuery<CrossCheckingKpi>({
    queryKey: BANKING_KEYS.crossCheckingKpi(currentOrganization?.id, filters),
    queryFn: async () => {
      await new Promise((resolve) => setTimeout(resolve, 300))
      return mockCrossCheckingKpi
    },
  })
}

/**
 * Hook to fetch cross-checking transactions filtered by tab and filters
 * Returns detailed transaction data for the cross-checking table
 */
export function useCrossCheckingTransactions(
  filters?: BankingFilters,
  tab: CrossCheckingTab = 'matching'
) {
  const { currentOrganization } = useOrganization()
  return useQuery<CrossCheckingTransaction[]>({
    queryKey: BANKING_KEYS.crossCheckingTransactions(currentOrganization?.id, filters, tab),
    queryFn: async () => {
      await new Promise((resolve) => setTimeout(resolve, 500))
      return mockCrossCheckingTransactions
    },
    // The key carries the date/facility filter and tab: keep the rows on
    // screen (dimmed) while the next filter loads instead of a skeleton.
    placeholderData: keepPreviousForOrg(currentOrganization?.id, 2),
  })
}

/**
 * Hook to fetch balance import status with server-side pagination + filters.
 *
 * Returns `{ items, total }`: one row per bank account on the current page
 * plus the total account count (drives the pagination footer, hidden at
 * ≤ 20 rows — see `lib/pagination.ts`). The
 * query joins bank_accounts + facilities + the latest `balances` snapshot
 * per account and computes an `isApiAccount` flag from `integration_id` —
 * the flag drives whether the inline-edit affordance is shown.
 *
 * `facilityIds` plumbs the page-level facility selector down to the SQL
 * so both the count and the page slice are scoped to the chosen facilities
 * — without it the table would show every account while the KPI cards above
 * already reflect a filtered view (DCP-120 Presto review feedback).
 *
 * Query key includes page/pageSize + facilityIds, so each (filter, page)
 * combo is cached independently and switching facilities doesn't invalidate
 * unrelated pages.
 */
export function useBalanceImportStatus(opts?: {
  page?: number
  pageSize?: number
  facilityIds?: string[]
}) {
  const { currentOrganization } = useOrganization()
  return useQuery<{ items: BalanceImportStatus[]; total: number }>({
    queryKey: BANKING_KEYS.balanceImportStatus(
      currentOrganization?.id,
      opts?.page,
      opts?.pageSize,
      opts?.facilityIds,
    ),
    queryFn: async () => {
      const { createTenantDb } = await import('@/lib/db-tenant')

      if (!currentOrganization?.id) {
        throw new Error('No organization selected')
      }

      const tenantDb = createTenantDb(currentOrganization.id)
      const { data, error } = await tenantDb.balances.listImportStatus(opts)
      if (error) throw new Error(error.message)
      return data
    },
    enabled: !!currentOrganization?.id,
    // The total travels with each page; keep it while the next page loads.
    placeholderData: keepPreviousForOrg(currentOrganization?.id, 2),
  })
}

/**
 * Hook to manually update a bank account's balance for today's date.
 *
 * Powers the inline edit on the Bank Balance Import Status table for
 * non-API accounts. The mutation upserts a `balances` row with
 * `source='manual'` and the current user's id in `updated_by` — this is
 * how the Balance Import History view (future) will attribute manual
 * entries to a person.
 *
 * Invalidates only the balance import status query — the page-level KPI
 * "Bank Balance" still reads from `plaid_balances_history` and is
 * intentionally left alone for this PR (see DCP-120 plan).
 */
export function useUpdateBankBalanceManual() {
  const queryClient = useQueryClient()
  const { currentOrganization } = useOrganization()
  const { user } = useAuth()

  return useMutation({
    mutationFn: async (params: { bankAccountId: string; balance: number }) => {
      const { createTenantDb } = await import('@/lib/db-tenant')

      if (!currentOrganization?.id) {
        throw new Error('No organization selected')
      }
      if (!user?.id) {
        // The hook is only mounted on authenticated pages, but guard the FK
        // contract: `balances.updated_by` references `public.profiles(id)`
        // and a NULL would still pass the FK — making the row unattributable
        // forever. Surface the missing-user condition instead of silently
        // recording an anonymous edit. The message is intentionally
        // user-facing (surfaced via the EditableBalanceCell error state) —
        // a stale session is the only realistic way to hit this branch on
        // a mounted page, so the actionable hint is "refresh".
        throw new Error('Your session has expired. Please refresh the page and try again.')
      }

      const tenantDb = createTenantDb(currentOrganization.id)
      const { error } = await tenantDb.balances.upsertManual({
        bankAccountId: params.bankAccountId,
        balance: params.balance,
        userId: user.id,
      })
      if (error) throw new Error(error.message)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: BANKING_KEYS.allBalanceImportStatus })
      // Bank Balance feeds the Cash Position Summary — refresh it too (DCP-143).
      queryClient.invalidateQueries({ queryKey: CASH_POSITION_SUMMARIES_KEY })
      // DCP-218 AC.2: a manual bank-balance edit directly changes the source
      // the Bank/Book Balance tiles aggregate (tenant `balances`),
      // so the KPI tiles must recalculate immediately, not after the 5min staleTime.
      queryClient.invalidateQueries({ queryKey: BANKING_KEYS.allKpi })
    },
  })
}

/**
 * Hook to update a manual transaction.
 * Invalidates manual transaction queries on success.
 */
export function useUpdateManualTransaction() {
  const queryClient = useQueryClient()
  const { currentOrganization } = useOrganization()

  return useMutation({
    mutationFn: async (params: { id: string; data: ManualTransactionInput }) => {
      const { createTenantDb } = await import('@/lib/db-tenant')

      if (!currentOrganization?.id) {
        throw new Error('No organization selected')
      }

      const tenantDb = createTenantDb(currentOrganization.id)
      const { data, error } = await tenantDb.manualTransactions.update(params.id, params.data)
      if (error) throw new Error(error.message)
      return data
    },
    onSuccess: () => {
      invalidateAfterManualTransactionWrite(queryClient, currentOrganization?.id)
      // The edited row may be file-imported: the import's details list shows
      // every field this edit can change.
      void queryClient.invalidateQueries({ queryKey: BANKING_KEYS.allFileImportTransactions })
    },
  })
}

/**
 * Query of {@link useDailyBankingTransactions} — the Daily Banking table read
 * (DCP-123). The filters match `bankTransactions.list`; `limit` / `offset`
 * absent = the unpaginated read behind the CSV export.
 */
export interface DailyBankingTransactionsQuery {
  startDate?: string
  endDate?: string
  facilityIds?: string[]
  accountIds?: string[]
  categories?: string[]
  /** DCP-123 (AC#6): server-side table sort; absent = newest first. */
  sort?: BankTransactionSort
  limit?: number
  offset?: number
}

/**
 * Read the Daily Banking transactions, sorted server-side (DCP-123, AC#6).
 *
 * WHY A SEPARATE HOOK: `useBankTransactions` (usePlaid.ts) does the same read
 * but its filter type has no `sort`, and usePlaid.ts is out of scope for this
 * ticket. Smuggling `sort` through its untyped pass-through would work only
 * for as long as that hook keeps forwarding its whole filters object.
 *
 * WHY THE KEY IS REUSED: the cache key is `PLAID_KEYS.transactions` —
 * `['plaid', 'transactions', orgId, query]` — exactly what
 * `useBankTransactions` writes. Every path that refreshes the table
 * invalidates that prefix (the sync-status hooks, LedgerSync completion in
 * `lib/ledgersync-sync-invalidation.ts`, disconnects, `useBankConnections`,
 * the Organization/Auth contexts clearing `['plaid']`, and
 * {@link useUpdateBankTransactionsCategory}), so a key of its own would
 * silently leave this read stale after each of them.
 *
 * A PAGED read (`offset` set) keeps the previous page on screen while the next
 * one loads (same organization only; `isPlaceholderData` tells the table). The
 * unpaginated export read never does: a CSV must not carry another filter's rows.
 *
 * @param query - filters, sort and pagination; must be memoized by the caller
 *        like any React Query key input (it is hashed, so a new-but-equal
 *        object only costs a re-hash)
 * @returns the React Query result over `TenantPlaidTransaction[]`; disabled
 *          until an organization is selected
 */
export function useDailyBankingTransactions(query: DailyBankingTransactionsQuery) {
  const { currentOrganization } = useOrganization()
  const orgId = currentOrganization?.id
  return useQuery({
    // Spread: PLAID_KEYS takes a Record, which an interface is not assignable to.
    queryKey: PLAID_KEYS.transactions(orgId, { ...query }),
    placeholderData: query.offset === undefined ? undefined : keepPreviousForOrg(orgId, 2),
    queryFn: async () => {
      const { createTenantDb } = await import('@/lib/db-tenant')

      if (!orgId) {
        throw new Error('No organization selected for tenant database access')
      }

      const tenantDb = createTenantDb(orgId)
      const { data, error } = await tenantDb.bankTransactions.list(query)
      if (error) throw error
      return data
    },
    enabled: !!orgId,
  })
}

/**
 * Prefix of every Daily Banking transaction query — the lists
 * (`PLAID_KEYS.transactions`) AND the pagination counts
 * (`PLAID_KEYS.transactionCount`). What a categorization invalidates (DCP-123).
 */
const BANK_TRANSACTIONS_PREFIX = ['plaid', 'transactions'] as const

/**
 * Mutation key shared by every in-flight category assignment (DCP-123) — lets
 * `onSettled` tell whether it is the LAST one still running (see there). Kept
 * out of the `['plaid', …]` QUERY namespace on purpose: it names a mutation,
 * and nothing that matches query keys by prefix should ever see it.
 */
const UPDATE_BANK_TRANSACTIONS_CATEGORY_KEY = ['banking', 'transactions', 'update-category'] as const

/** Variables of {@link useUpdateBankTransactionsCategory}. */
export interface UpdateBankTransactionsCategoryVariables {
  /** Transactions to (re)categorize — one for the inline dropdown, many for the bulk action. */
  ids: string[]
  /** The category to assign; its name/type drive the optimistic cell render. */
  category: AssignableCategory
}

/** Per-mutation context: the list cache entries overwritten optimistically, for rollback. */
interface UpdateBankTransactionsCategoryContext {
  previous: Array<[QueryKey, TenantPlaidTransaction[] | undefined]>
}

/**
 * Roll back ONE failed category assignment (DCP-123) — per row, not per cache
 * entry.
 *
 * Restoring the whole snapshotted arrays would also revert every OTHER edit
 * applied after this snapshot: inline edits overlap (row 1, then row 2 while 1
 * is still saving), and if row 2's save succeeded and row 1's fails, a
 * wholesale restore would put row 2's old category back on screen until the
 * next refetch. So only the rows in `ids` are taken from the snapshot and
 * written into the CURRENT entries; everything else in them is left as is.
 *
 * A row is restored only while it still shows THIS mutation's category — if a
 * later edit of the same row has repainted it since, that newer value wins
 * (and the settle-time invalidation reconciles whatever the server holds).
 * Entries that were never loaded, or have been removed since, are skipped.
 *
 * @param queryClient - the React Query client holding the transaction lists
 * @param previous - the snapshot `onMutate` took of the org's list entries
 * @param ids - the transactions this mutation patched optimistically
 * @param categoryId - the category this mutation painted onto them
 */
function restoreCategorizedRows(
  queryClient: QueryClient,
  previous: UpdateBankTransactionsCategoryContext['previous'],
  ids: string[],
  categoryId: string
): void {
  const idSet = new Set(ids)
  for (const [key, data] of previous) {
    if (!data) continue
    const snapshotById = new Map<string, TenantPlaidTransaction>()
    for (const row of data) {
      if (idSet.has(row.id)) snapshotById.set(row.id, row)
    }
    if (snapshotById.size === 0) continue
    queryClient.setQueryData<TenantPlaidTransaction[]>(key, (current) =>
      current?.map((row) => {
        const original = snapshotById.get(row.id)
        return original && row.category_id === categoryId ? original : row
      })
    )
  }
}

/**
 * Assign a category to Daily Banking transactions (DCP-123, AC#2/#3 — "saved
 * immediately"). Resolves with the number of rows written.
 *
 * OPTIMISTIC: the Category dropdown and the yellow "uncategorized" highlight
 * change the moment the user picks, not one round trip later. `onMutate`
 * cancels in-flight transaction LIST reads of the current organization (so a
 * late response can't overwrite the patch), snapshots them and patches the
 * matching rows in place. The prefix `['plaid', 'transactions', orgId]`
 * matches exactly the lists (`PLAID_KEYS.transactions` → `[…, orgId, query]`):
 * the pagination counts sit under `[…, 'count', orgId, …]` and so are never
 * touched (they hold numbers), and neither is another organization's cache.
 * On error only this mutation's rows are restored from the snapshot (see
 * {@link restoreCategorizedRows} — a wholesale restore would undo concurrent
 * edits) and the raw error is toasted; the success message, if any, is the
 * UI's call.
 *
 * INVALIDATION, and why so wide: the list / count must drop rows that no longer
 * match an active Category filter (hence the org-agnostic
 * {@link BANK_TRANSACTIONS_PREFIX}, which covers the counts); the
 * Category-filter options change; the KPI tiles honour the category filter and
 * sign uncategorized rows differently (DCP-124); the Manual Transactions page
 * lists file-import rows this can recategorize; and a (re)categorization to or
 * from LINE OF CREDIT moves LOC Usage, the LOC drill-down and — since DCP-147 —
 * the Cash Position Summary (same set as `useUpdateManualTransaction`, see its
 * DCP-290 / DCP-147 notes). It runs on FAILURE too (`onSettled`): the write
 * itself is one atomic RPC call (`update_transactions_category`), so a failed
 * one committed nothing, but the rows may still have changed underneath —
 * deleted, or recategorized by someone else — and the refetch is what makes the
 * table show the server's state after the rollback.
 *
 * A caller without `manage_banking` is rejected by that RPC with 42501
 * ("Insufficient permissions to categorize transactions"); the raw message is
 * what gets toasted. The page hides the controls for such users, so this is
 * the server-side backstop rather than an expected path.
 *
 * Invalidation waits for the LAST concurrent assignment to settle: several
 * inline edits in a row are normal here, and a refetch triggered by the first
 * one to finish could land before the others commit and briefly revert their
 * optimistic cells.
 *
 * A request that writes ZERO rows rejects: every requested row vanished in the
 * meantime (tenant tables carry no RLS policy that could filter an UPDATE), so
 * resolving would let the UI report a save that did not happen.
 */
export function useUpdateBankTransactionsCategory() {
  const queryClient = useQueryClient()
  const { currentOrganization } = useOrganization()
  const orgId = currentOrganization?.id

  return useMutation<number, Error, UpdateBankTransactionsCategoryVariables, UpdateBankTransactionsCategoryContext>({
    mutationKey: UPDATE_BANK_TRANSACTIONS_CATEGORY_KEY,
    mutationFn: async ({ ids, category }) => {
      const { createTenantDb } = await import('@/lib/db-tenant')

      if (!orgId) {
        throw new Error('No organization selected')
      }

      const tenantDb = createTenantDb(orgId)
      const { data, error } = await tenantDb.bankTransactions.updateCategory(ids, category.id)
      if (error) throw new Error(error.message)
      if (ids.length > 0 && data === 0) {
        // No "the table has been refreshed" here: invalidation waits for the
        // last concurrent assignment (see onSettled), so it may not have happened.
        throw new Error('The selected transactions no longer exist.')
      }
      return data
    },
    onMutate: async ({ ids, category }) => {
      const listsKey = [...BANK_TRANSACTIONS_PREFIX, orgId] as const
      await queryClient.cancelQueries({ queryKey: listsKey })
      const previous = queryClient.getQueriesData<TenantPlaidTransaction[]>({ queryKey: listsKey })

      const idSet = new Set(ids)
      const patchedCategory = { name: category.name, type: category.type }
      for (const [key, data] of previous) {
        // An entry created but never resolved (first load still pending) has
        // nothing to patch — and must not be seeded with an empty list.
        if (!data) continue
        queryClient.setQueryData<TenantPlaidTransaction[]>(key, (rows) =>
          rows?.map((row) =>
            idSet.has(row.id) ? { ...row, category_id: category.id, categories: patchedCategory } : row
          )
        )
      }
      return { previous }
    },
    onError: (error, { ids, category }, context) => {
      if (context) restoreCategorizedRows(queryClient, context.previous, ids, category.id)
      toast.error(error.message)
    },
    onSettled: () => {
      // This mutation still counts as running inside its own onSettled.
      if (queryClient.isMutating({ mutationKey: UPDATE_BANK_TRANSACTIONS_CATEGORY_KEY }) > 1) return
      void queryClient.invalidateQueries({ queryKey: BANK_TRANSACTIONS_PREFIX })
      void queryClient.invalidateQueries({ queryKey: ['plaid', 'categories'] })
      void queryClient.invalidateQueries({ queryKey: BANKING_KEYS.allKpi })
      void queryClient.invalidateQueries({ queryKey: BANKING_KEYS.allManualTransactions })
      void queryClient.invalidateQueries({ queryKey: BANKING_KEYS.allManualTransactionCounts })
      void queryClient.invalidateQueries({ queryKey: BANKING_KEYS.locAvailability(orgId) })
      void queryClient.invalidateQueries({ queryKey: BANKING_KEYS.locTransactions(orgId) })
      void queryClient.invalidateQueries({ queryKey: CASH_POSITION_SUMMARIES_KEY })
      // A file-imported row's category also shows in its import's details list.
      void queryClient.invalidateQueries({ queryKey: BANKING_KEYS.allFileImportTransactions })
    },
  })
}

/**
 * Hook to delete a manual transaction.
 * Invalidates manual transaction and KPI queries on success.
 */
export function useDeleteManualTransaction() {
  const queryClient = useQueryClient()
  const { currentOrganization } = useOrganization()

  return useMutation({
    mutationFn: async (id: string) => {
      const { createTenantDb } = await import('@/lib/db-tenant')

      if (!currentOrganization?.id) {
        throw new Error('No organization selected')
      }

      const tenantDb = createTenantDb(currentOrganization.id)
      const { error } = await tenantDb.manualTransactions.delete(id)
      if (error) throw new Error(error.message)
    },
    onSuccess: () => {
      invalidateAfterManualTransactionWrite(queryClient, currentOrganization?.id)
      // The Manual list includes file-imported rows, so the deleted row may
      // belong to a file import: its details list and count (also shown in the
      // delete-import confirmation) must not keep the old number.
      void queryClient.invalidateQueries({ queryKey: BANKING_KEYS.allFileImportTransactions })
    },
  })
}

/**
 * Hook to create multiple manual transactions in a single batch.
 * Invalidates manual transaction and KPI queries on success.
 */
export function useCreateManualTransactions() {
  const queryClient = useQueryClient()
  const { currentOrganization } = useOrganization()

  return useMutation({
    mutationFn: async (inputs: ManualTransactionInput[]) => {
      const { createTenantDb } = await import('@/lib/db-tenant')

      if (!currentOrganization?.id) {
        throw new Error('No organization selected')
      }

      const tenantDb = createTenantDb(currentOrganization.id)
      const { data, error } = await tenantDb.manualTransactions.createMany(inputs)
      if (error) throw new Error(error.message)
      return data
    },
    onSuccess: () => invalidateAfterManualTransactionWrite(queryClient, currentOrganization?.id),
  })
}

/**
 * Hook to create multiple bank accounts in a single batch.
 * Invalidates bank accounts query on success.
 */
export function useCreateBankAccounts() {
  const queryClient = useQueryClient()
  const { currentOrganization } = useOrganization()

  return useMutation({
    mutationFn: async (inputs: BankAccountInput[]) => {
      const { createTenantDb } = await import('@/lib/db-tenant')

      if (!currentOrganization?.id) {
        throw new Error('No organization selected')
      }

      const tenantDb = createTenantDb(currentOrganization.id)
      const { data, error } = await tenantDb.bankAccounts.createMany(inputs)
      if (error) throw new Error(error.message)
      return data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: BANKING_KEYS.allBankAccounts })
      // Balance Import Status (DCP-120) is a join of bank_accounts + balances;
      // any add/update/delete/rename of a bank account changes the row set or
      // its display fields, so refetch the import status too.
      queryClient.invalidateQueries({ queryKey: BANKING_KEYS.allBalanceImportStatus })
      // Bank accounts determine which accounts contribute to the Cash Position
      // Summary's Bank Balance — refresh it too (DCP-143).
      queryClient.invalidateQueries({ queryKey: CASH_POSITION_SUMMARIES_KEY })
      // DCP-218 AC.2: adding accounts changes the set summed into the balance
      // KPI tiles, so recalculate them too.
      queryClient.invalidateQueries({ queryKey: BANKING_KEYS.allKpi })
      // DCP-374: manual cards are ON the Credit Card Availability page (AC#1),
      // so creating, re-typing, re-assigning or deleting one changes the rows
      // the RPC returns. Without this the page serves the pre-change answer for
      // the whole 5-minute staleTime, and with refetchOnWindowFocus off a
      // re-navigation inside that window does not fix it (PR #238 review).
      queryClient.invalidateQueries({ queryKey: BANKING_KEYS.allCreditCardAvailability })
    },
  })
}

/**
 * Hook to update a manual bank account.
 * Invalidates bank accounts query on success.
 */
export function useUpdateBankAccount() {
  const queryClient = useQueryClient()
  const { currentOrganization } = useOrganization()

  return useMutation({
    mutationFn: async (params: { id: string; data: BankAccountUpdate }) => {
      const { createTenantDb } = await import('@/lib/db-tenant')

      if (!currentOrganization?.id) {
        throw new Error('No organization selected')
      }

      const tenantDb = createTenantDb(currentOrganization.id)
      const { data, error } = await tenantDb.bankAccounts.update(params.id, params.data)
      if (error) throw new Error(error.message)
      return data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: BANKING_KEYS.allBankAccounts })
      // Balance Import Status (DCP-120) is a join of bank_accounts + balances;
      // any add/update/delete/rename of a bank account changes the row set or
      // its display fields, so refetch the import status too.
      queryClient.invalidateQueries({ queryKey: BANKING_KEYS.allBalanceImportStatus })
      // Bank accounts determine which accounts contribute to the Cash Position
      // Summary's Bank Balance — refresh it too (DCP-143).
      queryClient.invalidateQueries({ queryKey: CASH_POSITION_SUMMARIES_KEY })
      // DCP-218 AC.2: an account's facility reassignment changes facility-scoped
      // balance KPI totals, so recalculate the tiles too.
      queryClient.invalidateQueries({ queryKey: BANKING_KEYS.allKpi })
      // DCP-324: a transaction's facility is now DERIVED from its account, so
      // reassigning an account rewrites the Facility column of its whole
      // history — and moves the rows in or out of the list entirely when the
      // new facility is inactive, or when the account had/gains none (AC#22).
      // Before this ticket the transaction carried its own stamped copy and a
      // reassignment left every cached transaction view correct, which is why
      // these keys were not here. The Dashboard table, its row count and its
      // Category-filter options live under the legacy-named 'plaid' keys (they
      // read the whole `transactions` table).
      queryClient.invalidateQueries({ queryKey: ['plaid', 'transactions'] })
      queryClient.invalidateQueries({ queryKey: ['plaid', 'categories'] })
      queryClient.invalidateQueries({ queryKey: BANKING_KEYS.allManualTransactions })
      queryClient.invalidateQueries({ queryKey: BANKING_KEYS.allManualTransactionCounts })
      // DCP-374: manual cards are ON the Credit Card Availability page (AC#1),
      // so this mutation changes the rows the RPC returns.
      queryClient.invalidateQueries({ queryKey: BANKING_KEYS.allCreditCardAvailability })
    },
  })
}

/**
 * Toggle whether MANUAL accounts count towards Bank Balance, the Bank Balance
 * Update table and the Cash Position Summary. The data layer refuses to
 * include a credit card; the error carries its message.
 *
 * Stays pending until the dependent queries have refetched, so the row's
 * toggle does not flip back to the old value while its list reloads.
 */
export function useSetManualIncludeInCashPosition() {
  const queryClient = useQueryClient()
  const { currentOrganization } = useOrganization()

  return useMutation({
    mutationFn: async ({ ids, include }: { ids: string[]; include: boolean }) => {
      if (!currentOrganization?.id) throw new Error('No organization selected')
      const tenantDb = createTenantDb(currentOrganization.id)
      const { error } = await tenantDb.bankAccounts.setIncludeInCashPosition(ids, include)
      if (error) throw new Error(error.message)
    },
    // The connected toggle's helper: it covers what this flag moves (KPI tiles,
    // Cash Position Summary, Balance Import Status, the bank-account lists) plus
    // connection-only keys, which are not mounted here and are only marked stale.
    onSuccess: () => invalidateAccountDependents(queryClient),
    // In the hook, like its connected twin, so no caller can drop the error:
    // a refusal (e.g. the account became a credit card meanwhile) must show.
    onError: (error) => toast.error(error.message),
  })
}

/**
 * Hook to rename a bank institution across all its manual accounts.
 * Updates institution_name on every account that currently has the old name.
 * Invalidates bank accounts query on success.
 */
export function useRenameBankInstitution() {
  const queryClient = useQueryClient()
  const { currentOrganization } = useOrganization()

  return useMutation({
    mutationFn: async (params: { oldName: string; newName: string }) => {
      const { createTenantDb } = await import('@/lib/db-tenant')

      if (!currentOrganization?.id) {
        throw new Error('No organization selected')
      }

      const tenantDb = createTenantDb(currentOrganization.id)
      const { error } = await tenantDb.bankAccounts.renameInstitution(params.oldName, params.newName)
      if (error) throw new Error(error.message)
    },
    onSuccess: () => invalidateAfterInstitutionRename(queryClient),
  })
}

/**
 * Hook to delete a manual bank account.
 * Invalidates bank accounts query on success.
 */
export function useDeleteBankAccount() {
  const queryClient = useQueryClient()
  const { currentOrganization } = useOrganization()

  return useMutation({
    mutationFn: async (id: string) => {
      const { createTenantDb } = await import('@/lib/db-tenant')

      if (!currentOrganization?.id) {
        throw new Error('No organization selected')
      }

      const tenantDb = createTenantDb(currentOrganization.id)
      const { error } = await tenantDb.bankAccounts.delete(id)
      if (error) throw new Error(error.message)
    },
    onSuccess: () => invalidateManualAccountRemoval(queryClient),
  })
}

/**
 * Delete a manual bank: soft-delete the given accounts (the bank's accounts as
 * Settings shows them) in one write. Related transactions are kept.
 */
export function useDeleteBankInstitution() {
  const queryClient = useQueryClient()
  const { currentOrganization } = useOrganization()

  return useMutation({
    mutationFn: async (accountIds: string[]) => {
      if (!currentOrganization?.id) {
        throw new Error('No organization selected')
      }
      const { error } = await createTenantDb(currentOrganization.id).bankAccounts.deleteMany(accountIds)
      if (error) throw new Error(error.message)
    },
    onSuccess: () => invalidateManualAccountRemoval(queryClient),
  })
}

/**
 * Normalized comparison key for a facility's Sage location mapping.
 *
 * The mapping is resolved case-insensitively everywhere (UPPER() on both sides
 * in the RPCs / backfill, `.toUpperCase()` in the sync task), so a case-only
 * edit resolves to the same facility and must NOT trigger a cache refresh.
 * NULL and '' both mean "unmapped" and normalize to the same empty key.
 *
 * @param value - Raw `sage_location_id` as stored or as entered in the form.
 * @returns Uppercased, trimmed key ('' when unmapped).
 */
function sageLocationKey(value: string | null | undefined): string {
  return (value ?? '').trim().toUpperCase()
}

/**
 * Re-derive the denormalized `sage_checks.facility_id` cache for an org.
 *
 * DCP-308 review #5: `facility_id` is written only by the migration backfill and
 * by the sync-checks task at insert time. The delta sync re-pulls a check only
 * when its Sage WHENMODIFIED falls inside the lookback window, so an untouched
 * check is never re-stamped — which makes a mapping change (a facility gaining or
 * changing its `sage_location_id`) permanently invisible to the Outstanding
 * Checks table, the Outstanding Checks tile and the Cash Position Summary, all
 * of which now read that column. This calls the server-side re-derivation so the
 * cache catches up the moment the mapping changes.
 *
 * Never throws: the facility itself was already saved when this runs, so a
 * failure here must not present as "the edit failed". The message is returned
 * for the caller to surface as a warning instead.
 *
 * @param orgId - Organization whose tenant checks are re-stamped.
 * @returns The error message when the refresh failed, otherwise null.
 */
async function refreshSageCheckFacilityIds(orgId: string): Promise<string | null> {
  try {
    const supabase = getSupabaseClient()
    const { error } = await supabase.rpc('refresh_sage_check_facility_ids', {
      p_org_id: orgId,
    })
    if (error) {
      console.error('[refreshSageCheckFacilityIds] RPC error:', error.message)
      return error.message
    }
    return null
  } catch (err) {
    const message = err instanceof Error ? err.message : String(err)
    console.error('[refreshSageCheckFacilityIds] threw:', message)
    return message
  }
}

/**
 * Invalidate every cache that reads `sage_checks.facility_id` after a refresh.
 *
 * The Outstanding Checks list/count (SAGE_KEYS.checks), the banking KPI tiles
 * (outstanding checks + the book balance derived from it) and the Cash Position
 * Summary all resolve checks through that column, so a re-stamp changes all
 * three. `SAGE_KEYS` is imported dynamically because useSageIntegration already
 * imports `BANKING_KEYS` from this module — a static import would close the cycle.
 *
 * @param queryClient - React Query client of the calling hook.
 * @param orgId - Organization the refresh ran against.
 */
async function invalidateSageCheckCaches(
  queryClient: ReturnType<typeof useQueryClient>,
  orgId: string,
): Promise<void> {
  const { SAGE_KEYS } = await import('@/hooks/useSageIntegration')
  queryClient.invalidateQueries({ queryKey: SAGE_KEYS.checks(orgId) })
  queryClient.invalidateQueries({ queryKey: BANKING_KEYS.allKpi })
}

/**
 * DCP-292 (AC2/AC3/AC5): invalidate every facility-SCOPED read cache after a
 * facility's active state may have changed (create / update / reactivate).
 *
 * Deactivating or reactivating a facility changes which rows and totals every
 * banking surface shows — the KPI tiles, the Dashboard chart, and the
 * manual-transactions / balance-import / outstanding-checks / projections
 * tables. None of these key off the facility LIST directly (they scope by
 * `is_active` inside their own queries/RPCs), so React Query must be told to
 * refetch them explicitly. Facility edits are infrequent settings actions, so
 * invalidating broadly (every org's entries under each key) is acceptable and
 * avoids threading the before/after `is_active` through every surface.
 */
async function invalidateFacilityScopedCaches(
  queryClient: ReturnType<typeof useQueryClient>,
  orgId: string,
): Promise<void> {
  queryClient.invalidateQueries({ queryKey: BANKING_KEYS.allKpi })
  queryClient.invalidateQueries({ queryKey: BANKING_KEYS.allManualTransactions })
  queryClient.invalidateQueries({ queryKey: BANKING_KEYS.allManualTransactionCounts })
  queryClient.invalidateQueries({ queryKey: BANKING_KEYS.allBalanceImportStatus })
  queryClient.invalidateQueries({ queryKey: BANKING_KEYS.allFacilityCategories })
  // Active-only bank-account views (Dashboard account filter, Settings → Banking
  // manual accounts) now key off active facilities — refresh them too.
  queryClient.invalidateQueries({ queryKey: BANKING_KEYS.allBankAccounts })
  // Banking Dashboard transactions table + row count + Category-filter options
  // (cached under the legacy-named 'plaid' transaction keys — they read the whole
  // `transactions` table) also scope to active facilities now.
  queryClient.invalidateQueries({ queryKey: ['plaid', 'transactions'] })
  queryClient.invalidateQueries({ queryKey: ['plaid', 'categories'] })
  queryClient.invalidateQueries({ queryKey: PROJECTION_KEYS.all })
  // AC5 for LOC Availability (PR #183 review #1). `get_loc_availability` and
  // `get_loc_transactions` both filter `f.is_active` (20260721000000_dcp290_loc_usage.sql
  // — this ticket's own migration header cites them as the precedent), so a
  // (de)activation genuinely changes what they return. Neither is reachable by
  // prefix from the keys above: they diverge at index 1 (`loc-availability` vs
  // `kpi` / `bank-accounts` / …), so without these two lines the LOC page kept
  // serving the pre-toggle answer for the full 5-minute staleTime — and with
  // `refetchOnWindowFocus: false` (queryClient.ts) plus React Query's
  // refetch-on-mount-only-when-stale, re-navigating to the page inside that
  // window does not refresh it either.
  //
  // `useUpdateManualTransaction` already invalidates exactly these two for a
  // weaker trigger (one edited transaction may reclassify a LOC row); a facility
  // being switched off is strictly the larger event.
  queryClient.invalidateQueries({ queryKey: BANKING_KEYS.allLocAvailability })
  queryClient.invalidateQueries({ queryKey: BANKING_KEYS.allLocTransactions })
  // DCP-374: the card page inner-joins facilities and filters on `is_active`,
  // so deactivating one removes its cards from the table outright.
  queryClient.invalidateQueries({ queryKey: BANKING_KEYS.allCreditCardAvailability })
  // A rename must reach the tables that show a facility's Display Name by join:
  // the file-import detail lists and the connection accounts tables.
  queryClient.invalidateQueries({ queryKey: BANKING_KEYS.allFileImportTransactions })
  queryClient.invalidateQueries({ queryKey: BANKING_KEYS.allFileImportProjections })
  queryClient.invalidateQueries({ queryKey: BANKING_KEYS.allConnectionAccounts })
  // Outstanding Checks list/count read through sage_checks.facility_id; SAGE_KEYS
  // is imported dynamically to avoid the useSageIntegration ↔ useBanking cycle.
  const { SAGE_KEYS } = await import('@/hooks/useSageIntegration')
  queryClient.invalidateQueries({ queryKey: SAGE_KEYS.checks(orgId) })
}

/**
 * What a facility create/update resolved to. A unique-name or Sage-id conflict
 * (`23505`) is an expected answer the form shows on the offending field, not a
 * failure, so it resolves instead of throwing; every other error still throws.
 */
export type FacilitySaveResult =
  | {
      status: 'saved'
      facility: TenantTables['facilities']['Row']
      orgId: string
      checksRefreshed: boolean
      checkRefreshError: string | null
    }
  | { status: 'duplicate'; duplicate: FacilityDuplicate }

/**
 * Find which submitted value a `23505` facility write collided with. Reads the
 * current facilities (one org fits in a single PostgREST page) rather than the
 * error text: constraint names differ between tenants (ADR 0021).
 */
async function explainFacilityWriteConflict(
  tenantDb: ReturnType<typeof createTenantDb>,
  submitted: SubmittedFacilityValues,
  editedId: string | null,
): Promise<FacilityDuplicate> {
  const { data, error } = await tenantDb.facilities.list()
  // The write was already refused as a duplicate, so the generic answer is
  // still true when the lookup itself fails.
  if (error) return { field: null, message: FACILITY_DUPLICATE_FALLBACK }
  return explainFacilityDuplicate(submitted, data, editedId)
}

/** Warning shown when the facility saved but its checks could not be re-stamped. */
export const SAGE_CHECK_REFRESH_WARNING =
  'Facility saved, but its Sage checks could not be re-linked. Outstanding Checks may be out of date until the next sync.'

/**
 * Hook to create a single facility.
 *
 * Mirrors `useUpdateFacility`: captures the active organisation id at the
 * start of the mutation and threads it through `onSuccess` so the cache
 * invalidation is scoped to the org the mutation actually ran against —
 * not the org that happens to be active when the request resolves.
 * Using a broader key (e.g. `['banking', 'facilities']` with no orgId)
 * would invalidate every cached organisation's list and trigger
 * unnecessary refetches whenever an admin user switches between orgs.
 *
 * @returns A React Query mutation hook with `mutateAsync(input)`.
 */
export function useCreateFacility() {
  const queryClient = useQueryClient()
  const { currentOrganization } = useOrganization()

  return useMutation({
    mutationFn: async (input: FacilityInput): Promise<FacilitySaveResult> => {
      const { createTenantDb } = await import('@/lib/db-tenant')

      const orgId = currentOrganization?.id
      if (!orgId) {
        throw new Error('No organization selected')
      }

      const tenantDb = createTenantDb(orgId)
      // pack_id is intentionally omitted — pack membership is managed only via
      // the Packs page (assignToPack); a new facility defaults to no pack.
      const { data, error } = await tenantDb.facilities.create({
        name: input.name,
        legal_name: input.legal_name,
        sage_location_id: input.sage_location_id,
        code: input.code ?? null,
        is_active: input.is_active ?? true,
      })
      if (error?.code === '23505') {
        return {
          status: 'duplicate',
          duplicate: await explainFacilityWriteConflict(tenantDb, input, null),
        }
      }
      if (error) throw new Error(error.message)

      // DCP-308 review #5, case A: a facility created WITH a Sage mapping
      // adopts checks that were synced before it existed — those rows carry
      // facility_id = NULL and the delta sync will never re-fetch them, so
      // re-derive the cache now. Unmapped facilities own no checks yet.
      const mapped = sageLocationKey(input.sage_location_id) !== ''
      const checkRefreshError = mapped ? await refreshSageCheckFacilityIds(orgId) : null

      return { status: 'saved', facility: data, orgId, checksRefreshed: mapped, checkRefreshError }
    },
    onSuccess: (result) => {
      if (result.status !== 'saved') return
      queryClient.invalidateQueries({
        queryKey: BANKING_KEYS.facilities(result.orgId),
      })
      // Facilities are the Cash Position Summary's columns (and carry the
      // sage_location_id mapping) — refresh it too (DCP-143).
      queryClient.invalidateQueries({ queryKey: CASH_POSITION_SUMMARIES_KEY })
      // DCP-292: a new facility (or its active state) changes what the tiles,
      // chart and dependent tables show — refresh them too.
      void invalidateFacilityScopedCaches(queryClient, result.orgId)
      if (result.checksRefreshed) {
        void invalidateSageCheckCaches(queryClient, result.orgId)
      }
    },
  })
}

const updateFacilityMutationKey = (orgId: string | undefined) =>
  ['banking', 'facilities', orgId, 'update'] as const

interface UpdateFacilityVariables {
  id: string
  data: FacilityUpdate
}

function isUpdateFacilityVariables(value: unknown): value is UpdateFacilityVariables {
  return (
    typeof value === 'object' &&
    value !== null &&
    'id' in value &&
    typeof value.id === 'string' &&
    'data' in value &&
    typeof value.data === 'object' &&
    value.data !== null
  )
}

/**
 * Hook to update a single facility.
 *
 * Sends a partial update (only provided fields are persisted) and invalidates
 * the facilities query on success so the settings list re-fetches with the
 * latest data. Errors surface via the mutation result so the caller can render
 * a toast — the hook itself does not show UI.
 *
 * @returns A React Query mutation hook with `mutateAsync({ id, data })`.
 */
export function useUpdateFacility() {
  const queryClient = useQueryClient()
  const { currentOrganization } = useOrganization()

  return useMutation({
    mutationKey: updateFacilityMutationKey(currentOrganization?.id),
    // Capture `orgId` at the start of the mutation and return it from the
    // mutation result so `onSuccess` invalidates the cache for the org the
    // mutation actually ran against. Using `currentOrganization?.id` from
    // the hook's closure inside `onSuccess` would invalidate the WRONG
    // org's cache if the user switches organisations between firing the
    // mutation and the mutation resolving — the old org's facility list
    // would silently keep stale data and the new (unaffected) org would
    // refetch for no reason.
    mutationFn: async (params: UpdateFacilityVariables): Promise<FacilitySaveResult> => {
      const { createTenantDb } = await import('@/lib/db-tenant')

      const orgId = currentOrganization?.id
      if (!orgId) {
        throw new Error('No organization selected')
      }

      const tenantDb = createTenantDb(orgId)

      // DCP-308 review #5, case B: read the mapping BEFORE the write so we can
      // tell whether `sage_location_id` actually changed. Only that field feeds
      // the `sage_checks.facility_id` cache, and re-deriving it is a full-table
      // statement — so a rename / code edit / active toggle must not pay for it.
      // A read failure is not fatal: fall back to "assume it changed" so the
      // cache is refreshed rather than silently left stale.
      let mappingChanged = false
      if (params.data.sage_location_id !== undefined) {
        const { data: before } = await tenantDb.facilities.getById(params.id)
        mappingChanged =
          before == null ||
          sageLocationKey(before.sage_location_id) !==
            sageLocationKey(params.data.sage_location_id)
      }

      const { data, error } = await tenantDb.facilities.update(params.id, params.data)
      if (error?.code === '23505') {
        return {
          status: 'duplicate',
          duplicate: await explainFacilityWriteConflict(tenantDb, params.data, params.id),
        }
      }
      if (error) throw new Error(error.message)

      // Re-stamp the checks only when the mapping really moved. This covers both
      // a facility gaining a mapping (its historical checks were stamped NULL)
      // and a re-map (its checks still point at the previous facility) — neither
      // of which the delta sync would ever correct on its own.
      const checkRefreshError = mappingChanged
        ? await refreshSageCheckFacilityIds(orgId)
        : null

      return {
        status: 'saved',
        facility: data,
        orgId,
        checksRefreshed: mappingChanged,
        checkRefreshError,
      }
    },
    onSuccess: async (result) => {
      if (result.status !== 'saved') return
      // Facilities are the Cash Position Summary's columns (and carry the
      // sage_location_id mapping) — refresh it too (DCP-143).
      queryClient.invalidateQueries({ queryKey: CASH_POSITION_SUMMARIES_KEY })
      // DCP-292 (AC2/AC3/AC5): deactivating/reactivating hides or restores this
      // facility's rows + totals across the app — refresh those caches.
      void invalidateFacilityScopedCaches(queryClient, result.orgId)
      if (result.checksRefreshed) {
        void invalidateSageCheckCaches(queryClient, result.orgId)
      }
      // The Packs table lists member facilities by name.
      queryClient.invalidateQueries({ queryKey: BANKING_KEYS.packs(result.orgId) })
      // Awaited so the mutation settles only once the list shows the write:
      // callers unlock their controls on settle, and a control unlocked over
      // the stale row invites a repeat click.
      await queryClient.invalidateQueries({
        queryKey: BANKING_KEYS.facilities(result.orgId),
      })
    },
  })
}

/**
 * The {@link useUpdateFacility} writes in flight in the current organization,
 * keyed by facility id, with the values each one sends — every write, not just
 * the latest, and from any caller (the Facilities list and the Edit dialog
 * alike). A write leaves the map once it settles, so a caller showing the
 * requested value falls back to the stored one on failure without a rollback.
 */
export function usePendingFacilityUpdates(): ReadonlyMap<string, FacilityUpdate> {
  const { currentOrganization } = useOrganization()
  const writes = useMutationState({
    filters: { mutationKey: updateFacilityMutationKey(currentOrganization?.id), status: 'pending' },
    select: (mutation) =>
      isUpdateFacilityVariables(mutation.state.variables) ? mutation.state.variables : null,
  })
  // Oldest first, so a later write to the same facility wins.
  return useMemo(
    () =>
      new Map(
        writes
          .filter((write): write is UpdateFacilityVariables => write !== null)
          .map((write) => [write.id, write.data]),
      ),
    [writes],
  )
}

/**
 * Hook to fetch all packs for the current organization (DCP-171).
 * Packs are returned ordered by name; membership is derived separately by
 * joining against `useFacilities` on `facility.pack_id` in the UI.
 */
export function usePacks() {
  const { currentOrganization } = useOrganization()
  return useQuery<Pack[]>({
    queryKey: BANKING_KEYS.packs(currentOrganization?.id),
    queryFn: async () => {
      const { createTenantDb } = await import('@/lib/db-tenant')
      if (!currentOrganization?.id) {
        throw new Error('No organization selected')
      }
      const tenantDb = createTenantDb(currentOrganization.id)
      const { data, error } = await tenantDb.packs.list()
      if (error) throw new Error(error.message)
      return data.map((p) => ({ id: p.id, name: p.name }))
    },
    enabled: !!currentOrganization?.id,
  })
}

/**
 * Coerce a PostgREST numeric (which may arrive as a string, or null for a
 * reference-only row) to a finite number. Non-finite / null collapses to 0 so
 * the LOC table never renders NaN.
 */
function num(value: number | string | null): number {
  const n = Number(value)
  return Number.isFinite(n) ? n : 0
}

/**
 * Hook to fetch the full Line of Credit Availability table for the current
 * organization (DCP-283).
 *
 * The `get_loc_availability` RPC returns FLAT rows — one per `loc_lines` row,
 * plus a single reference-only row (all `line_*` / `starting_*` fields null at
 * runtime) for a reference that has no line yet. This hook groups those rows
 * into the nested `LocReference[]` the page consumes: one entry per
 * `reference_id`, each nesting its `LocLine[]`.
 *
 * A FACILITY reference with no line is given exactly one synthetic empty line
 * so its row still renders and the Update dialog can create on first save. A
 * PACK reference with no line is left with an empty `lines` array — the Update
 * dialog seeds its own empty line for a fresh pack (DCP-167 multi-line editing).
 *
 * NUMERIC columns surface as strings over PostgREST, so every amount is coerced
 * via {@link num}.
 */
export function useLocAvailability() {
  const { currentOrganization } = useOrganization()
  const id = currentOrganization?.id
  return useQuery<LocReference[]>({
    queryKey: BANKING_KEYS.locAvailability(id),
    queryFn: async () => {
      if (!id) {
        throw new Error('No organization selected')
      }
      const { data, error } = await getSupabaseClient().rpc('get_loc_availability', {
        p_org_id: id,
      })
      if (error) throw new Error(error.message)

      // Group the flat rows into references, preserving RPC row order (the RPC
      // already sorts packs-first then name-ASC). A Map keeps first-seen order.
      const byReference = new Map<string, LocReference>()
      for (const row of data ?? []) {
        let reference = byReference.get(row.reference_id)
        if (!reference) {
          reference = {
            id: row.reference_id,
            name: row.reference_name,
            type: row.reference_type as 'pack' | 'facility',
            facilities: row.facility_names ?? [],
            lines: [],
          }
          byReference.set(row.reference_id, reference)
        }
        // Only rows carrying a real line contribute a LocLine. `line_id` is
        // typed non-null by the generated types but is null at runtime for a
        // reference-only row (a reference with no line yet).
        const lineId = row.line_id as string | null
        if (lineId != null) {
          const startingAmount = row.starting_loc_usage_amount as number | string | null
          // A line's own facilities come from `line_facility_names` (a pack line
          // covers only its own subset; a standalone-facility line covers just
          // that facility). Falls back to the reference's `facility_names` only
          // if the RPC returns null for a line-bearing row (defensive).
          const lineFacilities =
            (row.line_facility_names as string[] | null) ?? row.facility_names ?? []
          reference.lines.push({
            id: lineId,
            name: row.line_name,
            facilities: lineFacilities,
            borrowingBaseAmount: num(row.borrowing_base_amount),
            locUsage: num(row.loc_usage),
            effectiveDate: row.effective_date ?? '',
            note: row.note ?? '',
            startingLocUsageDate: row.starting_loc_usage_date ?? '',
            startingLocUsageAmount: startingAmount == null ? null : num(startingAmount),
          })
        }
      }

      // Synthesize one empty line for any FACILITY reference with no line, so
      // its row renders and the Update dialog can create on first save. Packs
      // with no line stay empty here — the dialog seeds a blank line for them.
      for (const reference of byReference.values()) {
        if (reference.type === 'facility' && reference.lines.length === 0) {
          const emptyLine: LocLine = {
            id: '',
            name: 'Line of Credit 1',
            facilities: reference.facilities,
            borrowingBaseAmount: 0,
            locUsage: 0,
            effectiveDate: '',
            note: '',
            startingLocUsageDate: '',
            startingLocUsageAmount: null,
          }
          reference.lines.push(emptyLine)
        }
      }

      return [...byReference.values()]
    },
    enabled: !!id,
  })
}

/**
 * `get_loc_transactions` output column each drill-down scope filters on: a
 * pack's child line row → `line_id` (DCP-290); a standalone facility row or a
 * Cash Position facility column → `facility_id`; a pack parent row or a Cash
 * Position pack-subtotal column → `pack_id` (both added by DCP-169).
 */
const SCOPE_COLUMN = { line: 'line_id', facility: 'facility_id', pack: 'pack_id' } as const

const EMPTY_CREDIT_CARDS: CreditCardAvailabilityRow[] = []

/**
 * One page of the Credit Card Availability table (DCP-374).
 *
 * Every filter, the search term, the ordering and the page itself travel to
 * `get_credit_card_availability`, so `query` must carry `page` and `pageSize`.
 * `totalCount` is the filter set's size, which the RPC repeats on every row.
 *
 * Keeps the previous answer while the next page (or a new filter set) loads,
 * so the rows and the footer do not collapse to the empty state and back —
 * but never across an org switch: another org's cards must not stand in for
 * this one's.
 *
 * @param query - filters, search, ordering and the page to fetch
 * @returns the page's rows, the filter set's total (undefined until the first
 *   answer), the query flags, and `retry` for a failed fetch
 */
export function useCreditCardAvailability(query: CreditCardAvailabilityQuery) {
  const { currentOrganization } = useOrganization()
  const orgId = currentOrganization?.id

  const result = useQuery({
    queryKey: BANKING_KEYS.creditCardAvailability(orgId, query),
    queryFn: async () => {
      if (!orgId) throw new Error('No organization selected')
      const { data, error } = await createTenantDb(orgId).creditCards.list(query)
      if (error) throw new Error(error.message)
      return data ?? { rows: [], totalCount: 0 }
    },
    enabled: !!orgId,
    placeholderData: keepPreviousForOrg(orgId, 2),
  })

  return {
    rows: result.data?.rows ?? EMPTY_CREDIT_CARDS,
    totalCount: result.data?.totalCount,
    isPending: result.isPending,
    isFetching: result.isFetching,
    isPlaceholderData: result.isPlaceholderData,
    error: result.error,
    retry: result.refetch,
  }
}

/**
 * Hook to fetch the real LINE OF CREDIT transactions behind ONE LOC Usage
 * "View bank transactions" pop-up (DCP-290, rescoped by DCP-333).
 *
 * The `get_loc_transactions` RPC returns one flat row per contributing
 * transaction, already filtered to the same facility set + starting-date cutoff
 * that make up a line's LOC Usage, and tagged with `line_id` / `facility_id` /
 * `pack_id`. This hook asks for the rows of the drilled reference only and
 * returns them as ready-to-render `BankTxnGroup[]`, one group per facility.
 *
 * WHY SCOPED (the DCP-333 bug): this hook used to call the RPC with no filter
 * and no pagination, i.e. every LOC transaction in the organization, and slice
 * the result client-side. PostgREST caps every response at its `db-max-rows`
 * (1000 on our projects) and TRUNCATES SILENTLY — no error, no header the client
 * checked. Note where that cap is configured, because it is easy to cite the
 * wrong artifact: `supabase/config.toml` sets it for the LOCAL stack ONLY (it
 * also pins localhost ports and disables TLS, so it cannot be governing a hosted
 * project). Deployed projects take it from the dashboard's API → Max rows, whose
 * default happens to be the same 1000 — measured on prod, where the unscoped
 * feed answered `206` with `Content-Range: 0-999/1211`. So changing config.toml
 * would not move the cap on any stand. Since the RPC orders by `line_id`, whichever
 * lines sorted past the cut received zero rows and their pop-up rendered empty,
 * while their LOC Usage figure stayed correct (it is a SQL aggregate inside
 * `get_loc_availability`, computed before any row limit applies). Same bug class
 * as {@link useSageChecksAggregateByEntity} in `@/hooks/useSageIntegration`,
 * where a truncated check register made per-entity counters read "0".
 *
 * Filtering server-side is what actually fixes it: PostgREST applies a chained
 * filter as an OUTER predicate over the function's result set — verified locally,
 * where `?nope=eq.1` on this RPC fails with `42703 column record.nope does not
 * exist`, i.e. the predicate is resolved against the returned record — and
 * therefore BEFORE the max_rows LIMIT. One reference's transactions are normally
 * orders of magnitude below 1000, so the realistic pop-up is served by a single
 * request. The residual case — one pack ABOVE the cap, measured as
 * `Content-Range: 0-999/1203` on a seeded pack — is not refused: the query
 * function WALKS the feed with `.range()` until it is exhausted and returns it
 * in full. Refusing was this fix's own leftover hole (found in DCP-333 senior
 * review): a reference past the cap threw on every click, so for exactly those
 * references the ticket's symptom ("LOC transactions are not displayed")
 * survived. `enabled` keeps the fetch from happening at all until a pop-up opens
 * (`scope !== null`), which also removes the page-load cost of the old org-wide
 * feed.
 *
 * NUMERIC/date columns surface as strings/ISO over PostgREST; amounts are
 * coerced via {@link num} and dates formatted for display.
 *
 * @param scope - the drilled reference, or null while no pop-up is open.
 * @returns React Query result with one group per contributing facility,
 * alphabetised by facility name, transactions newest-first within a group.
 */
export function useLocTransactions(scope: LocTxnScope | null) {
  const { currentOrganization } = useOrganization()
  const id = currentOrganization?.id
  return useQuery<BankTxnGroup[]>({
    queryKey: BANKING_KEYS.locTransactionsScoped(id, scope),
    // `signal` is React Query's per-fetch AbortSignal, forwarded to EVERY page
    // request below (DCP-333, PR #184 review). It earns its keep only because
    // this query is a WALK: the common single-page drill is over before anything
    // could cancel it, but a 1200+ row pack costs 2-3 SEQUENTIAL requests, so a
    // user clicking through several references would otherwise leave each
    // earlier walk running to completion, spending network and DB time on rows
    // nobody will read. Reading `signal` at all is also what opts this query
    // into React Query's cancel-on-unmount path — query-core only calls
    // `retryer.cancel()` when the signal was consumed, otherwise it merely
    // stops retrying — which is exactly the "closed the pop-up mid-walk" case.
    // An aborted page comes back as an ordinary error RESULT rather than a
    // rejection (postgrest-js converts fetch failures into `{ error }`), so the
    // loop's existing `if (error) throw` ends the walk at that page instead of
    // marching on to the next offset.
    queryFn: async ({ signal }) => {
      if (!id) {
        throw new Error('No organization selected')
      }
      // Unreachable while `enabled` holds; narrows `scope` for the filter below
      // and fails loudly rather than silently fetching an unscoped feed if a
      // future caller ever forces the query.
      if (!scope) {
        throw new Error('No LOC transactions scope selected')
      }
      // Hoisted out of the walk below (DCP-333): `scope` is a PARAMETER, so
      // TypeScript drops the null-narrowing from the guard above inside the
      // `fetchPage` closure (a parameter behaves like `let` — it could in
      // principle be reassigned before the closure runs). Capturing the two
      // values the request needs as consts keeps the closure free of a non-null
      // assertion.
      const scopeColumn = SCOPE_COLUMN[scope.kind]
      const scopeId = scope.id

      // One request per page. 1000 matches PostgREST's `db-max-rows` on our
      // projects, so asking for more can never be served in full anyway.
      const PAGE = 1000
      // Runaway backstops, NOT business rules (DCP-333). This feed is ONE
      // reference's LOC transactions, so both ceilings sit far above anything
      // real — the largest measured reference is ~1.2k rows. They exist because
      // the loop's terminators trust the server: a server that keeps answering
      // non-empty pages (a proxy replaying the same page, an `.eq` silently
      // dropped upstream) would otherwise spin the tab forever. The request cap
      // is separate from the row cap because a server capping pages far below
      // `PAGE` needs proportionally more requests for the same rows.
      const MAX_ROWS = 50_000
      const MAX_PAGES = 200

      /**
       * Fetch ONE page of the scoped feed.
       *
       * @param from - zero-based offset of the first row wanted.
       * @param withCount - request `count: 'exact'`, i.e. PostgREST's
       * window-function count of the FILTERED set, computed before the max_rows
       * LIMIT applies and therefore the authoritative total for the whole feed.
       * Passed on the FIRST page only: the total does not change between pages,
       * so re-counting would make every later page pay for a second aggregate
       * over the same set for nothing.
       */
      const fetchPage = (from: number, withCount: boolean) =>
        getSupabaseClient()
          .rpc(
            'get_loc_transactions',
            { p_org_id: id },
            // `as const` keeps 'exact' from widening to `string` through the
            // ternary, which would not satisfy the options type.
            withCount ? ({ count: 'exact' } as const) : {},
          )
          // No cast needed here despite the generated `Returns` type predating
          // DCP-169 (see the row-widening cast below, which is still required):
          // postgrest-js types `.eq()` as `<ColumnName extends string>`, so an
          // unknown column name compiles, and the VALUE type resolves across this
          // union through `line_id` — the one scope column the stale type does
          // know — down to `string`. So `scope.id` stays type-checked and the
          // filter needs no `any`; PostgREST resolves the column at runtime.
          .eq(scopeColumn, scopeId)
          // Ordering is now this hook's job, not the RPC's: the modal shows
          // facilities alphabetically and transactions newest-first, and the
          // deterministic order is also what makes the first-seen grouping below
          // stable across refetches. `id` last is the unique tiebreaker that makes
          // the sort TOTAL (DCP-333): several transactions on one date are the
          // norm here, and without it Postgres may return those ties in any order,
          // so a background refetch could silently reshuffle rows under the reader
          // (and, mid-edit, under the row the draft state is keyed to).
          //
          // The total order is ALSO what makes the offset walk below correct, and
          // is why paginating is safe now and would not have been before: over a
          // non-total order the server may return tied rows in a different
          // arrangement per request, so a row can appear on two pages or on
          // none. With `id` as the final key every page is a slice of one fixed
          // sequence, so `.range()` can neither duplicate nor skip a row.
          .order('facility_name', { ascending: true })
          .order('transaction_date', { ascending: false })
          .order('id')
          .range(from, from + PAGE - 1)
          // Cancellation is per REQUEST, so the signal has to be attached to
          // every page and not just the first one — the later pages are the
          // whole point (see the note on `signal` above).
          .abortSignal(signal)

      // Walk the whole scoped feed rather than refuse a capped one (DCP-333):
      // refusing left a reference above `db-max-rows` permanently un-drillable,
      // with no in-app recourse — every click refetched, got 1000 of N and threw.
      const rows: NonNullable<Awaited<ReturnType<typeof fetchPage>>['data']> = []
      /**
       * Widened shape of one `get_loc_transactions` row (DCP-333, PR #184
       * review finding 12). The generated `Returns` type predates DCP-169,
       * which added `id`, `source`, `facility_id` and `pack_id` to the RPC's
       * RETURNS TABLE — `pnpm db:types` was never re-run against that
       * migration, so both `idOf` and the grouping pass below need this same
       * widening to read those columns. Declared once and reused by both
       * instead of two separate inline casts, so the row shape they agree on
       * is written down in one place. The real fix is regenerating
       * `packages/shared/src/types/database.ts`; once that happens this alias
       * — and every cast built from it — can simply be deleted.
       */
      type LocTxnRawRow = (typeof rows)[number] & {
        id: string
        source: string | null
        facility_id: string
        pack_id: string | null
      }
      /**
       * A row's transaction id, widened via {@link LocTxnRawRow} (see that
       * alias for why). Used by the shifted-window accounting below, which
       * has to run per PAGE and therefore cannot wait for the grouping pass.
       */
      const idOf = (row: (typeof rows)[number]) => (row as LocTxnRawRow).id
      // Ids received on any EARLIER page, and how many rows repeated one of them
      // — the completeness check after the grouping pass explains both.
      const idsFromEarlierPages = new Set<string>()
      let shiftedDuplicates = 0
      // Authoritative feed size from the first page, or null when unknown.
      let total: number | null = null
      let pages = 0
      let from = 0
      for (;;) {
        const { data, error, count } = await fetchPage(from, pages === 0)
        // Any failing page aborts the whole walk: a half-built result must never
        // reach the cache, where it would be indistinguishable from a complete
        // drill-down.
        if (error) throw new Error(error.message)
        const page = data ?? []
        rows.push(...page)
        // Shifted-window accounting for the completeness check after the grouping
        // pass (DCP-333, PR #184 review). The two kinds of duplicate mean
        // opposite things and must not be lumped together. A repeat INSIDE one
        // page is the server's own: one request is one snapshot, so a row can
        // only appear twice there through a join fan-out, and PostgREST's `count`
        // — computed over that same result set — counts both copies, so
        // collapsing it later costs the feed nothing. A repeat ACROSS pages is
        // the offset race: rows were inserted above the cursor, every later row
        // shifted down, and this copy stands where an unseen row would have been.
        // Hence membership is tested against earlier pages ONLY, before this
        // page's own ids join the set. Deliberately conservative in one alignment:
        // a fan-out pair split exactly by a page boundary (identical ids are
        // adjacent under the total order, so it takes that exact straddle) is
        // charged as a shift and refuses a complete feed — a visible error rather
        // than a silent short list, which is the direction to err in.
        const pageIds = page.map(idOf)
        shiftedDuplicates += pageIds.filter((pageId) => idsFromEarlierPages.has(pageId)).length
        for (const pageId of pageIds) idsFromEarlierPages.add(pageId)
        pages += 1
        if (pages === 1) {
          // NaN guard: a non-exact `Content-Range: 0-999/*` parses to NaN in
          // postgrest-js. Treat that, like an absent header, as "size unknown"
          // instead of letting NaN poison every comparison below.
          total = count !== null && Number.isFinite(count) ? count : null
        }
        // Primary terminator: the server told us how big the feed is. Counted
        // RAW (duplicates included) on purpose — this is a termination
        // condition, not an integrity one; the deduped completeness check after
        // the grouping pass is what catches a walk that stopped here too early.
        if (total !== null && rows.length >= total) break
        // Fallback terminator for `total === null` (a proxy stripped
        // `content-range`, or it came back non-exact). A SHORT page must NOT
        // stand in for it: the server may legitimately cap a page below `PAGE`,
        // so "fewer rows than asked for" does not mean "end of feed". In this
        // degraded path we therefore deliberately pay one extra round trip that
        // comes back empty rather than guess and cut the tail off a financial
        // list.
        if (page.length === 0) break
        if (rows.length >= MAX_ROWS || pages >= MAX_PAGES) {
          throw new Error(
            `Loading this reference's LOC transactions was stopped after ` +
              `${rows.length.toLocaleString('en-US')} rows over ${pages} requests, ` +
              `past the safety limits of ${MAX_ROWS.toLocaleString('en-US')} rows and ` +
              `${MAX_PAGES} requests. The list would be incomplete, so nothing is shown.`,
          )
        }
        // Advance by what the server ACTUALLY sent, never by `PAGE`: if
        // `db-max-rows` is lower than `PAGE` on some project, `from += PAGE`
        // would step straight over the rows it declined to send.
        from += page.length
      }

      // Group flat rows by facility ID; first-seen insertion order is
      // alphabetised by facility name via the `.order()` above. Keyed by ID, NOT
      // by name (DCP-333): `facilities` carries no UNIQUE on `name` — not even
      // per pack, as 20260714010000_dcp283_dcp167_loc_rpcs.sql spells out where
      // it counts matches before resolving a name ("There is no UNIQUE(pack_id,
      // name) on facilities") — so two same-named member facilities of one pack
      // would collapse into a SINGLE group, silently breaking DCP-169's "one
      // group per pack member" and hiding one facility's rows inside the other's.
      // The name is display-only here, so it stays as the group heading.
      const byFacility = new Map<string, BankTxnGroup>()
      // Dedupe by transaction id across the whole result (carried over from
      // `collectFacilityTxns` in CashPositionPage, PR #169 review). Defence
      // only: duplicates cannot occur against the deployed
      // `get_loc_transactions` (20260731010000_dcp169_loc_txn_notes_editable),
      // because each facility reaches the feed through exactly one line — a pack
      // facility via `loc_line_facilities` (UNIQUE facility_id,
      // `loc_line_facilities_facility_unique`), a standalone facility via
      // `loc_lines.facility_id` (UNIQUE, `loc_lines_facility_unique`) — and the
      // two branches are mutually exclusive: the standalone branch is guarded by
      // `f.pack_id IS NULL` and the pack branch by `f.pack_id = ll.pack_id`. So
      // even the orphaned standalone `loc_lines` row left behind by a pack move
      // (see `assignToPack`) no longer re-enters the feed, which is what used to
      // double a transaction, double its total and put both copies into edit
      // mode. Kept because that guard lives in SQL that a future migration could
      // relax, and the failure mode is a wrong money figure.
      const seen = new Set<string>()
      for (const row of rows) {
        // Same widening as `idOf` above, via the shared {@link LocTxnRawRow}
        // alias — see its comment for why the generated `Returns` type still
        // needs it.
        const r = row as LocTxnRawRow
        // `r` IS `row` — one object, one read: the cast widens the static TYPE
        // and copies/transforms nothing. Both spellings appear below (the
        // DCP-169 columns through `r`, the already-typed ones through `row`), so
        // this note exists purely so they are never mistaken for two different
        // rows (DCP-333, PR #184 review).
        if (seen.has(r.id)) continue
        seen.add(r.id)
        let group = byFacility.get(r.facility_id)
        if (!group) {
          // `id` carries the grouping key THROUGH to the render layer (DCP-333,
          // PR #184 review). Keying this Map by facility id kept two same-named
          // member facilities apart here, but the modal flattens the groups and
          // decided its heading boundaries by comparing NAMES — so the two
          // groups merged back into one heading downstream and the split was
          // lost precisely in the case it was built for. Setting it is not
          // optional on this side: the modal only falls back to the name when
          // `id` is absent.
          group = { id: r.facility_id, name: row.facility_name, transactions: [] }
          byFacility.set(r.facility_id, group)
        }
        group.transactions.push({
          // DCP-169: id + source drive inline Notes editing in the modal — the
          // id is the key `updateNotes` writes against, source gates whether the
          // row is editable (manual/file_import only).
          id: r.id,
          source: r.source ?? '',
          // DCP-169: facility_id / pack_id identify the transaction's facility
          // and owning pack (null for a standalone facility). Since DCP-333 they
          // are simply the columns the SERVER-side scope filter narrows on — the
          // flat-feed caller that used to re-scope them client-side is gone. Kept
          // per row because they cost nothing and are what tells two same-named
          // facilities apart when debugging a group.
          facilityId: r.facility_id,
          packId: r.pack_id,
          date: row.transaction_date ? formatDate(row.transaction_date) : '',
          account: row.account_name ?? '',
          accountNumber: row.account_number ?? '',
          amount: num(row.amount),
          description: row.description ?? '',
          memo: row.memo ?? '',
          notes: row.notes ?? '',
        })
      }

      // Completeness backstop, DEDUPED rather than raw (DCP-333, PR #184
      // review). Offset pagination is NOT snapshot-isolated, so between two page
      // requests an insert landing ABOVE the cursor shifts every later row down
      // (a row is served twice) and a delete shifts them up (a row is skipped).
      // Either alone is safe or already caught: a lone duplicate is collapsed by
      // `seen` above while `from` advances by rows actually RECEIVED, and a lone
      // delete leaves the walk visibly short. TOGETHER they are neither — the
      // duplicate pads raw `rows.length` up to `total`, ending the walk, while
      // the rendered set is one or more real transactions short. A raw check then
      // reads `total === total`, passes, and the dedupe silently swallows the
      // loss: the very failure this rescoping exists to remove, merely relocated
      // out of PostgREST's row cap and into a race between our own requests. So
      // the figure compared has to be the one we are about to RENDER, which is
      // why this sits after the grouping pass.
      //
      // `coveredRows` is that figure: rows received, minus only the copies
      // charged to a shifted window (see the accounting in the walk). It is NOT
      // `seen.size`, and that distinction is load-bearing — `seen` also collapses
      // a duplicate the SERVER emitted within one page, which PostgREST's `count`
      // counted twice, so comparing `seen.size` would turn the PR #169 fan-out
      // defence into a hard error and make such a reference un-drillable. Adds no
      // false positives otherwise: rows deleted between the count and the fetch
      // already tripped the previous raw check, and a duplicate that costs the
      // feed nothing (insert above the cursor, nothing deleted) still reconciles.
      //
      // Refusing beats rendering: the shortfall cannot be reconciled
      // client-side (a dropped tail netting to ~0 looks perfectly consistent),
      // and a financial list quietly missing rows under a confident-looking Total
      // is worse than one that says it is incomplete. The message surfaces
      // through the modal's existing `errorMessage` path (which also blanks the
      // Total), needing no new prop and no change to this hook's
      // `BankTxnGroup[]` return type; reopening the pop-up re-walks the by-then
      // settled feed. `total === null` means unknown, not short — don't block on
      // it.
      const coveredRows = rows.length - shiftedDuplicates
      if (total !== null && coveredRows < total) {
        throw new Error(
          `This reference reported ${total} LOC transactions but only ${coveredRows} distinct ` +
            'transactions could be loaded, so the list and its total would be incomplete. ' +
            'Please reopen the pop-up to try again.',
        )
      }

      return [...byFacility.values()]
    },
    // Nothing is fetched until a pop-up opens — the whole point of DCP-333.
    enabled: !!id && scope !== null,
    // Reopening the same pop-up (a common back-and-forth while reconciling) is
    // served from cache; mutations still invalidate the `locTransactions(orgId)`
    // prefix, so an edited note shows up immediately.
    staleTime: 30_000,
    // No automatic retry (DCP-333), overriding the global "retry once" policy in
    // `@/lib/queryClient`. Three reasons, all specific to this query now being a
    // multi-request walk: (1) a retry repeats the ENTIRE walk, so a failure that
    // cost N requests costs 2N, and the pop-up keeps spinning through the retry
    // backoff before it finally says anything; (2) the global policy's "skip 4xx"
    // branch cannot fire here at all — PostgREST errors are rewrapped as
    // `new Error(error.message)`, which carries no `status` — so the single retry
    // it would perform is exactly the useless kind, a permission error tried
    // twice; (3) the runaway-guard throw is deterministic for a misbehaving
    // server, so retrying it only doubles the worst case. Manual recourse is one
    // click: a failed query holds no data, so closing and reopening the pop-up
    // re-walks from scratch.
    retry: false,
  })
}

/**
 * Hook to persist an inline Notes edit from the "View bank transactions"
 * drill-down modal (DCP-169).
 *
 * Delegates to `manualTransactions.updateNotes`, which merges the `notes` key
 * into the transaction's `metadata` and is source-scoped to
 * MANUAL_PAGE_TRANSACTION_SOURCES ('manual','file_import'). The modal is the
 * gate — it renders non-editable sources read-only, so this only ever runs for
 * an editable row; a stray call on any other source matches zero rows and
 * errors (not a silent write) — a backstop, not the primary guard. Mirrors
 * {@link useUpdateManualTransaction}: `createTenantDb` for the current org,
 * throw on the returned DbResult error so the caller's try/catch surfaces the
 * raw message inline.
 */
export function useUpdateTransactionNotes() {
  const queryClient = useQueryClient()
  const { currentOrganization } = useOrganization()

  return useMutation({
    mutationFn: async ({ id, notes }: { id: string; notes: string }) => {
      const { createTenantDb } = await import('@/lib/db-tenant')

      if (!currentOrganization?.id) {
        throw new Error('No organization selected')
      }

      const tenantDb = createTenantDb(currentOrganization.id)
      const { error } = await tenantDb.manualTransactions.updateNotes(id, notes)
      if (error) throw new Error(error.message)
    },
    onSuccess: () => {
      // The drill-down modal reads from get_loc_transactions — refresh it so the
      // saved note replaces the row's previous value in place.
      void queryClient.invalidateQueries({
        queryKey: BANKING_KEYS.locTransactions(currentOrganization?.id),
      })
      // DCP-169: the same note is a column on the Manual Transactions table
      // (Banking tabs), so its list + count queries must refetch too — otherwise
      // the edit is only reflected in the modal, not on the tabs.
      void queryClient.invalidateQueries({ queryKey: BANKING_KEYS.allManualTransactions })
      void queryClient.invalidateQueries({ queryKey: BANKING_KEYS.allManualTransactionCounts })
    },
  })
}

/**
 * Hook to create-or-update the Borrowing Base (and its dates / note / starting
 * usage) for a standalone facility's single LOC line (DCP-283).
 *
 * Mirrors {@link useUpdateSageCheck}: an optimistic patch of the
 * `locAvailability` cache before the round-trip, rollback + toast on error, and
 * a settle-time invalidation so the cache converges on server truth. Editing a
 * borrowing base changes the Cash Position Summary's LOC availability and the
 * banking KPI tiles, so both are invalidated too.
 *
 * Empty-string date / nullable fields are mapped to null before persisting.
 *
 * Persists via the `save_facility_borrowing_base` RPC (DCP-167) rather than a
 * direct table write: the RPC enforces `manage_loc` (and org access)
 * server-side, so a `view_only` user can no longer bypass the permission gate
 * by calling the table directly. The old `locLines.upsertForFacility` direct
 * write had no way to be permission-gated.
 */
export function useUpdateBorrowingBase() {
  const queryClient = useQueryClient()
  const { currentOrganization } = useOrganization()
  const orgId = currentOrganization?.id

  return useMutation({
    mutationFn: async ({
      facilityId,
      line,
    }: {
      facilityId: string
      line: {
        name: string
        borrowingBaseAmount: number
        effectiveDate: string
        note: string
        startingLocUsageDate: string
        startingLocUsageAmount: number | null
      }
    }) => {
      if (!orgId) {
        throw new Error('No organization selected')
      }
      // `p_line` mirrors the single-element shape `save_pack_borrowing_base`
      // uses per line, minus `sequence`/`facilities` — the RPC fixes
      // sequence=1 and resolves facilities from the facility itself.
      const { data, error } = await getSupabaseClient().rpc('save_facility_borrowing_base', {
        p_org_id: orgId,
        p_facility_id: facilityId,
        p_line: {
          name: line.name,
          borrowing_base_amount: line.borrowingBaseAmount,
          // Empty strings from the form's date inputs become NULL columns.
          effective_date: line.effectiveDate || null,
          note: line.note || null,
          starting_loc_usage_date: line.startingLocUsageDate || null,
          starting_loc_usage_amount: line.startingLocUsageAmount,
        },
      })
      if (error) throw new Error(error.message)
      return data
    },
    /**
     * Optimistically patch the edited facility reference's single line in the
     * cached `locAvailability` snapshot so the table reflects the new values
     * instantly. The original snapshot is captured for rollback in `onError`.
     */
    onMutate: async ({ facilityId, line }) => {
      await queryClient.cancelQueries({ queryKey: BANKING_KEYS.locAvailability(orgId) })

      const previous = queryClient.getQueryData<LocReference[]>(
        BANKING_KEYS.locAvailability(orgId),
      )

      queryClient.setQueryData<LocReference[]>(
        BANKING_KEYS.locAvailability(orgId),
        (old) => {
          if (!old) return old
          return old.map((reference) => {
            if (reference.id !== facilityId) return reference
            const existing = reference.lines[0]
            // Preserve id + usage (usage is derived, not edited here); overwrite
            // the editable fields from the form.
            const patchedLine: LocLine = {
              id: existing?.id ?? '',
              name: line.name,
              facilities: existing?.facilities ?? reference.facilities,
              borrowingBaseAmount: line.borrowingBaseAmount,
              locUsage: existing?.locUsage ?? 0,
              effectiveDate: line.effectiveDate,
              note: line.note,
              startingLocUsageDate: line.startingLocUsageDate,
              startingLocUsageAmount: line.startingLocUsageAmount,
            }
            return { ...reference, lines: [patchedLine] }
          })
        },
      )

      return { previous }
    },
    onError: (error: Error, _variables, context) => {
      // Restore the pre-mutation snapshot; the settle-time refetch below then
      // reconciles with server truth regardless.
      if (context?.previous) {
        queryClient.setQueryData(BANKING_KEYS.locAvailability(orgId), context.previous)
      }
      // Same surfacing as every other mutation in this file: the server's own
      // message, with a generic fallback. The form validates these fields
      // client-side (DCP-307), so an error here only fires on a race, a stale
      // client, or a direct API call.
      toast.error(error.message || 'Failed to update borrowing base')
    },
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: BANKING_KEYS.locAvailability(orgId) })
      // Starting LOC Usage Date/Amount changes shift the drill-down's date
      // cutoff, so refresh the LINE OF CREDIT transactions feed too — otherwise
      // the "View bank transactions" modal keeps a stale set until staleTime.
      void queryClient.invalidateQueries({ queryKey: BANKING_KEYS.locTransactions(orgId) })
      // A borrowing-base change feeds the banking KPI tiles and the Cash
      // Position Summary's LOC availability — refresh both.
      void queryClient.invalidateQueries({ queryKey: BANKING_KEYS.allKpi })
      void queryClient.invalidateQueries({ queryKey: CASH_POSITION_SUMMARIES_KEY })
    },
  })
}

/**
 * Hook to replace a pack's full set of borrowing-base LOC lines (DCP-167).
 *
 * Unlike {@link useUpdateBorrowingBase} (single standalone-facility line), a
 * pack owns many lines and each line covers a subset of the pack's member
 * facilities. The write is REPLACE-semantics: `p_lines` is the FULL desired
 * list and `save_pack_borrowing_base` reconciles inserts/updates/deletes in one
 * RPC. Facilities are passed as NAMES (members of the pack); an unknown name
 * makes the RPC raise.
 *
 * Mirrors {@link useUpdateBorrowingBase}: optimistic patch of the pack
 * reference's `lines` in the `locAvailability` cache, rollback + toast on error,
 * and a settle-time invalidation of the LOC table plus the KPI tiles and Cash
 * Position Summary (both consume borrowing-base figures).
 */
export function useUpdatePackBorrowingBase() {
  const queryClient = useQueryClient()
  const { currentOrganization } = useOrganization()
  const orgId = currentOrganization?.id

  return useMutation({
    mutationFn: async ({ packId, lines }: { packId: string; lines: LocLine[] }) => {
      if (!orgId) {
        throw new Error('No organization selected')
      }
      // Map each editable line to the RPC's element shape. `sequence` is the
      // 1-based visual order; empty-string dates collapse to null columns.
      const { data, error } = await getSupabaseClient().rpc('save_pack_borrowing_base', {
        p_org_id: orgId,
        p_pack_id: packId,
        p_lines: lines.map((l, i) => ({
          name: l.name,
          borrowing_base_amount: l.borrowingBaseAmount,
          effective_date: l.effectiveDate || null,
          note: l.note || null,
          sequence: i + 1,
          starting_loc_usage_date: l.startingLocUsageDate || null,
          starting_loc_usage_amount: l.startingLocUsageAmount,
          facilities: l.facilities,
        })),
      })
      if (error) throw new Error(error.message)
      return data
    },
    /**
     * Optimistically replace the edited pack reference's `lines` in the cached
     * `locAvailability` snapshot so the table reflects the new set instantly.
     * The original snapshot is captured for rollback in `onError`.
     */
    onMutate: async ({ packId, lines }) => {
      await queryClient.cancelQueries({ queryKey: BANKING_KEYS.locAvailability(orgId) })

      const previous = queryClient.getQueryData<LocReference[]>(
        BANKING_KEYS.locAvailability(orgId),
      )

      queryClient.setQueryData<LocReference[]>(
        BANKING_KEYS.locAvailability(orgId),
        (old) => {
          if (!old) return old
          return old.map((reference) =>
            reference.id === packId ? { ...reference, lines: [...lines] } : reference,
          )
        },
      )

      return { previous }
    },
    onError: (error: Error, _variables, context) => {
      // Restore the pre-mutation snapshot; the settle-time refetch below then
      // reconciles with server truth regardless.
      if (context?.previous) {
        queryClient.setQueryData(BANKING_KEYS.locAvailability(orgId), context.previous)
      }
      // Same surfacing as every other mutation in this file: the server's own
      // message, with a generic fallback. The form validates these fields
      // client-side (DCP-307), so an error here only fires on a race, a stale
      // client, or a direct API call.
      toast.error(error.message || 'Failed to update borrowing base')
    },
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: BANKING_KEYS.locAvailability(orgId) })
      // Starting LOC Usage Date/Amount changes shift the drill-down's date
      // cutoff, so refresh the LINE OF CREDIT transactions feed too — otherwise
      // the "View bank transactions" modal keeps a stale set until staleTime.
      void queryClient.invalidateQueries({ queryKey: BANKING_KEYS.locTransactions(orgId) })
      // A borrowing-base change feeds the banking KPI tiles and the Cash
      // Position Summary's LOC availability — refresh both.
      void queryClient.invalidateQueries({ queryKey: BANKING_KEYS.allKpi })
      void queryClient.invalidateQueries({ queryKey: CASH_POSITION_SUMMARIES_KEY })
    },
  })
}

/**
 * Hook to create a pack and assign its facilities (DCP-171).
 *
 * Two-step operation: insert the pack row, then stamp `pack_id` on the
 * selected facilities. If the assignment fails, the just-created pack is
 * deleted (manual rollback) so a failed create never leaves an empty pack
 * behind — the Supabase client has no multi-statement transaction. Both the
 * packs and facilities caches are invalidated on success.
 */
export function useCreatePack() {
  const queryClient = useQueryClient()
  const { currentOrganization } = useOrganization()

  return useMutation({
    mutationFn: async (input: PackInput) => {
      const { createTenantDb } = await import('@/lib/db-tenant')
      const orgId = currentOrganization?.id
      if (!orgId) {
        throw new Error('No organization selected')
      }
      const tenantDb = createTenantDb(orgId)

      const { data: pack, error } = await tenantDb.packs.create({ name: input.name })
      if (error) {
        // 23505 = unique_violation on packs.name — surface a clear message
        // inline rather than leaking the raw Postgres constraint error.
        if (error.code === '23505') throw new Error('A pack with this name already exists')
        throw new Error(error.message)
      }

      // Assign the chosen facilities to the freshly-created pack. On failure,
      // roll back by deleting the pack so we don't leave it empty.
      const { error: assignError } = await tenantDb.facilities.assignToPack(
        input.facility_ids,
        pack.id,
      )
      if (assignError) {
        // Roll back the just-created pack. If the rollback itself fails we
        // can't recover here, so log it (an empty orphaned pack may remain and
        // need manual cleanup) and still surface the original error.
        const { error: rollbackError } = await tenantDb.packs.delete(pack.id)
        if (rollbackError) {
          console.error(
            `Failed to roll back pack ${pack.id} ("${input.name}") after facility assignment error; an empty pack may remain`,
            rollbackError,
          )
        }
        throw new Error(assignError.message)
      }

      return { pack, orgId }
    },
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: BANKING_KEYS.packs(result.orgId) })
      queryClient.invalidateQueries({ queryKey: BANKING_KEYS.facilities(result.orgId) })
      // The Cash Position Summary (DCP-145) groups by pack and sums per-pack, so a
      // new pack (and its facility assignment) changes its columns/subtotals.
      queryClient.invalidateQueries({ queryKey: CASH_POSITION_SUMMARIES_KEY })
      // DCP-374: the page's Pack filter reads `facilities.pack_id`, so a pack
      // change alters both the filter options and the rows behind them.
      queryClient.invalidateQueries({ queryKey: BANKING_KEYS.allCreditCardAvailability })
    },
  })
}

/**
 * Hook to update a pack's name and/or membership (DCP-171).
 *
 * Order matters (no DB transaction available):
 *  1. Reconcile membership first — assign the new selection, then release the
 *     de-selected members (`clearPackExcept`). Assigning first means the
 *     intermediate state never leaves the pack empty.
 *  2. Rename last. Membership is the operation that can fail with PACK_CONFLICT;
 *     doing it before the rename means a conflict aborts with the pack fully
 *     unchanged (no half-renamed state), rather than a new name + old members.
 */
export function useUpdatePack() {
  const queryClient = useQueryClient()
  const { currentOrganization } = useOrganization()

  return useMutation({
    mutationFn: async (params: { id: string; data: PackUpdate }) => {
      const { createTenantDb } = await import('@/lib/db-tenant')
      const orgId = currentOrganization?.id
      if (!orgId) {
        throw new Error('No organization selected')
      }
      const tenantDb = createTenantDb(orgId)

      // 1. Reconcile membership first (when provided): assign the new selection,
      //    then release members that are no longer selected. This is the step
      //    that can fail with PACK_CONFLICT, so running it before the rename
      //    keeps a conflict from leaving the pack half-updated.
      if (params.data.facility_ids !== undefined) {
        const { error: assignError } = await tenantDb.facilities.assignToPack(
          params.data.facility_ids,
          params.id,
        )
        if (assignError) throw new Error(assignError.message)
        const { error: clearError } = await tenantDb.facilities.clearPackExcept(
          params.id,
          params.data.facility_ids,
        )
        if (clearError) throw new Error(clearError.message)
      }

      // 2. Rename last (when provided).
      if (params.data.name !== undefined) {
        const { error } = await tenantDb.packs.update(params.id, { name: params.data.name })
        if (error) {
          if (error.code === '23505') throw new Error('A pack with this name already exists')
          throw new Error(error.message)
        }
      }

      return { orgId }
    },
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: BANKING_KEYS.packs(result.orgId) })
      queryClient.invalidateQueries({ queryKey: BANKING_KEYS.facilities(result.orgId) })
      // A rename or membership change alters the Cash Position Summary's pack
      // columns/subtotals (DCP-145), so refresh it too.
      queryClient.invalidateQueries({ queryKey: CASH_POSITION_SUMMARIES_KEY })
      // DCP-374: the page's Pack filter reads `facilities.pack_id`, so a pack
      // change alters both the filter options and the rows behind them.
      queryClient.invalidateQueries({ queryKey: BANKING_KEYS.allCreditCardAvailability })
    },
  })
}

/**
 * Hook to delete a pack (DCP-171).
 *
 * The `facilities.pack_id` FK is `ON DELETE SET NULL`, so deleting a pack
 * un-assigns its facilities without deleting them (AC #6). The facilities
 * cache is invalidated so their (now cleared) pack assignment refreshes.
 */
export function useDeletePack() {
  const queryClient = useQueryClient()
  const { currentOrganization } = useOrganization()

  return useMutation({
    mutationFn: async (id: string) => {
      const { createTenantDb } = await import('@/lib/db-tenant')
      const orgId = currentOrganization?.id
      if (!orgId) {
        throw new Error('No organization selected')
      }
      const tenantDb = createTenantDb(orgId)
      const { error } = await tenantDb.packs.delete(id)
      if (error) throw new Error(error.message)
      return { orgId }
    },
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: BANKING_KEYS.packs(result.orgId) })
      queryClient.invalidateQueries({ queryKey: BANKING_KEYS.facilities(result.orgId) })
      // Deleting a pack drops its column from the Cash Position Summary and
      // un-assigns its facilities (they resurface as standalone), so refresh it.
      queryClient.invalidateQueries({ queryKey: CASH_POSITION_SUMMARIES_KEY })
      // DCP-374: the page's Pack filter reads `facilities.pack_id`, so a pack
      // change alters both the filter options and the rows behind them.
      queryClient.invalidateQueries({ queryKey: BANKING_KEYS.allCreditCardAvailability })
    },
  })
}

/**
 * Hook to fetch file import records.
 * Returns a paginated list of file imports for the current organization,
 * optionally filtered by status.
 * @param filters - Optional status filter and pagination controls
 */
export function useFileImports(filters?: { status?: string; limit?: number; offset?: number; importType?: string }) {
  const { currentOrganization } = useOrganization()
  const query = useQuery<FileImportRow[]>({
    queryKey: BANKING_KEYS.fileImports(currentOrganization?.id, filters),
    queryFn: async () => {
      const { createTenantDb } = await import('@/lib/db-tenant')
      if (!currentOrganization?.id) throw new Error('No organization selected')
      const tenantDb = createTenantDb(currentOrganization.id)
      const { data, error } = await tenantDb.fileImports.list(filters)
      if (error) throw new Error(error.message)
      return data ?? []
    },
    enabled: !!currentOrganization?.id,
    // Poll every 2s while any import is still plausibly in progress.
    //
    // The age check is the load-bearing part: a row whose task never started or
    // was killed mid-run keeps a non-terminal status forever, and the previous
    // status-only condition turned that single row into a request every 2s, for
    // every visitor of the page, indefinitely (DCP-298 review). See
    // `shouldPollFileImports` for why 15 minutes is the cutoff.
    refetchInterval: (query) => (shouldPollFileImports(query.state.data) ? 2000 : false),
    placeholderData: keepPreviousForOrg(currentOrganization?.id, 2),
  })
  return query
}

/**
 * Hook to fetch the total count of file imports for the current organization.
 * Used for pagination calculations in the import history section.
 * @param filters - Optional status filter to match the list query
 */
export function useFileImportsCount(filters?: { status?: string; importType?: string }) {
  const { currentOrganization } = useOrganization()
  return useQuery<number>({
    queryKey: [...BANKING_KEYS.fileImports(currentOrganization?.id, filters), 'count'],
    queryFn: async () => {
      const { createTenantDb } = await import('@/lib/db-tenant')
      if (!currentOrganization?.id) throw new Error('No organization selected')
      const tenantDb = createTenantDb(currentOrganization.id)
      const { data, error } = await tenantDb.fileImports.count(filters)
      if (error) throw new Error(error.message)
      return data ?? 0
    },
    enabled: !!currentOrganization?.id,
  })
}

/**
 * Hook to fetch a single file import by ID.
 * Automatically polls every 2 seconds while the import is 'pending' or 'processing',
 * and stops polling once it reaches a terminal state ('completed' or 'failed').
 * @param fileImportId - UUID of the file import record, or null to disable
 */
export function useFileImportStatus(fileImportId: string | null) {
  const { currentOrganization } = useOrganization()
  return useQuery<FileImportRow | null>({
    queryKey: BANKING_KEYS.fileImportById(currentOrganization?.id, fileImportId ?? ''),
    queryFn: async () => {
      const { createTenantDb } = await import('@/lib/db-tenant')
      if (!currentOrganization?.id || !fileImportId) return null
      const tenantDb = createTenantDb(currentOrganization.id)
      const { data, error } = await tenantDb.fileImports.getById(fileImportId)
      if (error) throw new Error(error.message)
      return data
    },
    enabled: !!currentOrganization?.id && !!fileImportId,
    // Poll while the job is still in progress, and only while it can still be
    // running. Status alone was not enough: a row abandoned by a task that never
    // started (or was killed at `maxDuration`) stays non-terminal forever, so
    // this poller kept issuing a request every 2s for the lifetime of the tab —
    // the same unbounded-poll defect that was fixed for the list in
    // `useFileImports`, and it must give up on the same terms (DCP-298 review).
    refetchInterval: (query) => {
      const row = query.state.data
      return row && isImportLive(row) ? 2000 : false
    },
  })
}

/**
 * Hook to create a file import record with status 'pending'.
 * Invalidates the file imports list cache on success so the new record appears immediately.
 */
export function useCreateFileImport() {
  const queryClient = useQueryClient()
  const { currentOrganization } = useOrganization()
  return useMutation<FileImportRow, Error, FileImportInput>({
    mutationFn: async (input: FileImportInput) => {
      const { createTenantDb } = await import('@/lib/db-tenant')
      if (!currentOrganization?.id) throw new Error('No organization selected')
      const tenantDb = createTenantDb(currentOrganization.id)
      const { data, error } = await tenantDb.fileImports.create(input)
      if (error) throw new Error(error.message)
      return data!
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: BANKING_KEYS.allFileImports })
    },
  })
}

/**
 * `placeholderData` for the import details lists: the previous page of the SAME
 * import stays on screen while the next one loads. The details modal keeps its
 * hooks mounted across imports, so the org guard alone would show import A's
 * rows under import B's title.
 */
function keepPreviousForImport(organizationId: string | undefined, fileImportId: string | null) {
  const keepForOrg = keepPreviousForOrg(organizationId, 2)
  return <T>(previous: T | undefined, previousQuery: { queryKey: readonly unknown[] } | undefined): T | undefined =>
    // Slot 3 of BANKING_KEYS.fileImportTransactions / fileImportProjections.
    previousQuery?.queryKey[3] === fileImportId ? keepForOrg(previous, previousQuery) : undefined
}

/**
 * Fetch paginated transactions belonging to a specific file import.
 * Only enabled when fileImportId is provided.
 *
 * @param fileImportId - UUID of the file import to fetch transactions for
 * @param filters - Optional pagination (limit, offset)
 */
export function useFileImportTransactions(
  fileImportId: string | null,
  filters?: { limit?: number; offset?: number },
) {
  const { currentOrganization } = useOrganization()
  return useQuery({
    queryKey: BANKING_KEYS.fileImportTransactions(currentOrganization?.id, fileImportId ?? '', filters),
    placeholderData: keepPreviousForImport(currentOrganization?.id, fileImportId),
    queryFn: async () => {
      const { createTenantDb } = await import('@/lib/db-tenant')
      const tenantDb = createTenantDb(currentOrganization!.id)
      return tenantDb.fileImports.listTransactions(fileImportId!, filters)
    },
    enabled: !!currentOrganization?.id && !!fileImportId,
  })
}

/**
 * Count transactions belonging to a specific file import (details modal
 * pagination, and the count the delete-import confirmation quotes).
 * Always re-read on mount: imported transactions can be deleted one by one on
 * the Manual tab, and the delete confirmation must not quote a cached figure.
 * Only enabled when fileImportId is provided.
 *
 * @param fileImportId - UUID of the file import to count transactions for
 */
export function useFileImportTransactionsCount(fileImportId: string | null) {
  const { currentOrganization } = useOrganization()
  return useQuery({
    queryKey: [...BANKING_KEYS.fileImportTransactions(currentOrganization?.id, fileImportId ?? ''), 'count'] as const,
    queryFn: async () => {
      const { createTenantDb } = await import('@/lib/db-tenant')
      const tenantDb = createTenantDb(currentOrganization!.id)
      return tenantDb.fileImports.countTransactions(fileImportId!)
    },
    enabled: !!currentOrganization?.id && !!fileImportId,
    refetchOnMount: 'always',
  })
}

/**
 * Fetch projections for a specific file import (for details modal).
 * Returns projections with joined category and facility names.
 * @param fileImportId - UUID of the file_imports record (null disables the query)
 * @param filters - Optional pagination
 */
export function useFileImportProjections(
  fileImportId: string | null,
  filters?: { limit?: number; offset?: number },
) {
  const { currentOrganization } = useOrganization()

  return useQuery({
    queryKey: BANKING_KEYS.fileImportProjections(currentOrganization?.id, fileImportId ?? '', filters),
    placeholderData: keepPreviousForImport(currentOrganization?.id, fileImportId),
    queryFn: async () => {
      const { createTenantDb } = await import('@/lib/db-tenant')
      const tenantDb = createTenantDb(currentOrganization!.id)
      return tenantDb.fileImports.listProjections(fileImportId!, filters)
    },
    enabled: !!currentOrganization?.id && !!fileImportId,
  })
}

/**
 * Count projections for a specific file import (details modal pagination, and
 * the count the delete-import confirmation quotes).
 * Always re-read on mount: projections can be deleted one by one after the
 * import, and the delete confirmation must not quote a cached figure.
 * @param fileImportId - UUID of the file_imports record (null disables the query)
 */
export function useFileImportProjectionsCount(fileImportId: string | null) {
  const { currentOrganization } = useOrganization()

  return useQuery({
    queryKey: [...BANKING_KEYS.fileImportProjections(currentOrganization?.id, fileImportId ?? ''), 'count'] as const,
    queryFn: async () => {
      const { createTenantDb } = await import('@/lib/db-tenant')
      const tenantDb = createTenantDb(currentOrganization!.id)
      return tenantDb.fileImports.countProjections(fileImportId!)
    },
    enabled: !!currentOrganization?.id && !!fileImportId,
    refetchOnMount: 'always',
  })
}
